import type EmberTransition from '@ember/routing/transition';
import Service, { type Registry as Services, service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import dayjs from 'dayjs';
import type { Hotel, Market, Ota } from 'frontend-utils/models';
import {
  BENI_COMPARISON_SETS,
  type ComparisonSetWithoutOwn,
} from 'frontend-utils/utils/constants/benchmark-insight';
import type { Platform } from 'frontend-utils/utils/constants/constant-values';
import {
  OTA_IDS,
  PARITY_STRATEGY_DATA_START,
  PLATFORMS,
} from 'frontend-utils/utils/constants/constant-values';
import Countries from 'frontend-utils/utils/countries';
import {
  OCCUPANCY_VIEWS,
  type OccupancyView,
} from 'lumen/components/hotel/benchmark-insight/dropdowns/dropdown-occupancy-view';
import {
  AVAILABLE_MEAL_PLANS as AVAILABLE_MEAL_PLANS_PARITY,
  AVAILABLE_PERSONS as AVAILABLE_PERSONS_PARITY,
  AVAILABLE_PLATFORMS as AVAILABLE_PLATFORMS_PARITY,
} from 'lumen/components/parity/selections';
import {
  DEFAULT_MARKET_COMPOSITION_TAB,
  MARKET_COMPOSITION_TABS,
} from 'lumen/controllers/hotel/benchmark-insight/market-composition';
import {
  CENTRAL_MAPPING_MENU_VIEWS,
  isCentralMappingMenuView,
} from 'lumen/controllers/hotel/settings/central-mapping/menu';
import type { BudgetForecastEntry, ParityConfig } from 'lumen/models';
import { ISSUE_TYPES, type IssueType } from 'lumen/models/parity-manager/issue';
import type { NetRevenueQueryParams } from 'lumen/routes/hotel/net-revenue';
import type { HotelSettingsCentralMappingMenuParams } from 'lumen/routes/hotel/settings/central-mapping/menu';
import type { MarketTotalRevenueParams } from 'lumen/routes/market/total-revenue';
import {
  BREAKDOWNS,
  CENTRAL_MAPPING_LOCAL_STORAGE_KEY_USER_SELECTED_PAGE_SIZE,
  DEFAULT_BREAKDOWN,
  DEFAULT_PAGE_SIZE as CENTRAL_MAPPING_DEFAULT_PAGE_SIZE,
  DEFAULT_VALUE_TYPE,
  isMappingBreakdown,
  PAGE_NUMBER_OPTIONS,
} from 'lumen/utils/central-mapping-constants';
import {
  BENI_COMPETITORS_TABS,
  BENI_PICKUP_DAYS,
  BENI_PICKUP_DAYS_DEFAULT,
  BENI_REFERENCE_PERIODS,
  BENI_STAY_DATE_SECTION_VIEWS,
  type BeniStayDateSectionView,
  type CompetitorsBenchmarkTab,
  type HeroChartAxisOption,
  type Kpi as BenchmarkKpi,
  KPI_INDEXOPTION,
  KPIS,
} from 'lumen/utils/constants/benchmark-insight/beni-constant-values';
import {
  DROPDOWN_COMPARISON_SETS_OPTION_NONE,
  type DropdownComparisonSetsOptionNone,
} from 'lumen/utils/constants/benchmark-insight/comparison-set-selection/dropdown-comparison-sets';
import {
  DROPDOWN_LOS_SORT_OPTIONS,
  type DropdownLosSortOption,
} from 'lumen/utils/constants/benchmark-insight/los/dropdown-los';
import { DEFAULT_QP_VALUES } from 'lumen/utils/constants/default-qp-values';
import { FEATURE_TOGGLES } from 'lumen/utils/constants/feature-toggle-constants';
import type {
  CompareType,
  MealTypeValues,
  RateParam,
} from 'lumen/utils/constants/general-constants';
import {
  AVAILABLE_RATECHANGES,
  COMPARETYPES,
  DATE_AGGREGATION,
  MAX_LOS_BOOKING,
  MAX_LOS_EXPEDIA,
  MEALTYPES,
  MEMBERSHIPTYPES,
  OTA_IDS_WITH_4_GUESTS,
  RATE_PARAMS,
} from 'lumen/utils/constants/general-constants';
import {
  ACTIVE_FILTERS,
  MAX_DAYS_IN_FUTURE,
} from 'lumen/utils/constants/market-insight-constants';
import {
  GROUP_BY,
  isGroupBy,
} from 'lumen/utils/constants/market-snapshots-constants';
import {
  COMPARISON_MODES,
  DATE_FORMAT,
} from 'lumen/utils/constants/net-revenue-constants';
import {
  DEFAULT_DAYS_SHOWN_IN_OD,
  OD_FB_FILTERS,
} from 'lumen/utils/constants/overview-dashboard-constants';
import type {
  DateAggregation,
  FromDateType,
  ParityFilter,
  RateType,
  SortType,
} from 'lumen/utils/constants/pi-constant-values';
import {
  DATE_AGGREGATION_MONTH,
  DATE_AGGREGATION_WEEK,
  DEFAULT_LOS,
  DEFAULT_POS,
  DIMENSIONS,
  FROM_DATE_TYPES,
  PARITY_FILTER,
  RATE_TYPES,
  SORT_TYPES,
} from 'lumen/utils/constants/pi-constant-values';
import { PMS_MAPPING_TABS_BENCHMARK } from 'lumen/utils/constants/pms-mapping-constants';
import {
  MAX_ALLOWED_MONTHS_IN_FUTURE,
  PRIMA_VIEWS,
} from 'lumen/utils/constants/pricing-assistant-constants';
import {
  DEFAULT_PACE_LEADTIME,
  isPaceLeadtime,
  KPI_CATEGORIES,
  MAX_AMOUNT_OF_FILTERS_PER_BLOCK,
  REVENUE_TABS,
  ROOMS_TABS,
  VALID_KPIS,
  VIEWS,
  WEEKS_IN_YEAR,
} from 'lumen/utils/constants/ri-constant-values';
import {
  ALLOWED_REFERENCE_PERIODS,
  MAX_NUMBER_OF_REFERENCE_PERIODS,
  METRICS as TOTAL_REVENUE_METRICS,
  VIEWS as TOTAL_REVENUE_VIEWS,
} from 'lumen/utils/constants/total-revenue-constants';
import getTodayUtc from 'lumen/utils/dates/get-today-utc';
import { isComparingRates } from 'lumen/utils/is-comparing-rates';
import type { MarketSnapshotsParams } from 'lumen/utils/market-snapshots/query';
import {
  getFirstAllowedHistoricalExtractDate,
  getFormattedLastPossibleExtractDate,
} from 'lumen/utils/parity-analytics/date-utils';
import { parseDateRangeString, parseObject } from 'lumen/utils/parse-utils';
import {
  getDefaultStayDateRange,
  getStayDateRangeQueryParams,
  getWeekForStayDateRange,
  isStayDateRangeEnabled,
} from 'lumen/utils/pricing-assistant/dynamic-rate-pricing-utils';
import getDateRange from 'lumen/utils/revenue-insight/get-date-range';

/**
 * Apparently `targetName` is a private API of transition so it's not in the types, but we do rely on it.
 *
 * @see https://github.com/emberjs/rfcs/issues/211
 */
type TransitionWithTarget = EmberTransition & { targetName: string };

/**
 * a more strict type for the transition.
 */
export type Transition = EmberTransition & {
  to: NonNullable<EmberTransition['to']>;
};
type QueryParams = NonNullable<EmberTransition['to']>['queryParams'];

function isPlatform(value: unknown): value is Platform {
  return Object.values(PLATFORMS).includes(value as Platform);
}

function isNumber(value: unknown): value is number {
  return typeof value === 'number' && !isNaN(value);
}

function isRateParam(value: unknown): value is RateParam {
  return Object.values(RATE_PARAMS).includes(value as RateParam);
}

function isMealType(value: unknown): value is MealTypeValues {
  return Object.values(MEALTYPES).includes(value as MealTypeValues);
}

function isRateType(value: unknown): value is RateType {
  return Object.values(RATE_TYPES).includes(value as RateType);
}

function isFromDateType(value: unknown): value is FromDateType {
  return Object.values(FROM_DATE_TYPES).includes(value as FromDateType);
}

export default class TransitionService extends Service {
  @service declare access: Services['access'];
  @service('benchmark-insight/beni-service')
  declare benchmarkInsightBeniService: Services['benchmark-insight/beni-service'];
  @service('benchmark-insight/features')
  declare benchmarkInsightFeatures: Services['benchmark-insight/features'];

  @service declare centralMapping: Services['central-mapping'];
  @service declare currencyConversion: Services['currency-conversion'];
  @service declare destination: Services['destination'];
  @service declare errorTracking: Services['error-tracking'];
  @service declare featureToggle: Services['feature-toggle'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare localStorage: Services['local-storage'];
  @service declare router: Services['router'];
  @service declare store: Services['store'];
  @service('total-revenue/date')
  declare totalRevenueDate: Services['total-revenue/date'];

  hasVisitedRevenueInsight = false;
  lastActiveHotelOrMarketId: string | null = null;

  get defaultPersons(): number {
    return this.hotelAndMarketService.hotelOrMarket.defaultPersons;
  }

  get hotelOrMarket(): Hotel | Market {
    return this.hotelAndMarketService.hotelOrMarket;
  }

  checkBenchmarkInsightCompetitorsActiveTab(
    params: QueryParams & { activeTab?: CompetitorsBenchmarkTab },
  ): void {
    const {
      hasCompsetBenchmark,
      hasSettingsBenchmarkPortfolioCompsetView,
      hasCompsetBenchmarkSmartCompset,
    } = this.access;
    const { COMPSET, PORTFOLIO_COMPSET, SMART_COMPSET } = BENI_COMPETITORS_TABS;

    const availableTabs: CompetitorsBenchmarkTab[] = [];
    if (hasCompsetBenchmark) {
      availableTabs.push(COMPSET);
    }
    if (hasSettingsBenchmarkPortfolioCompsetView) {
      availableTabs.push(PORTFOLIO_COMPSET);
    }
    if (hasCompsetBenchmarkSmartCompset) {
      availableTabs.push(SMART_COMPSET);
    }

    // `activeTab` will be `undefined` when it's the default value (= `compset`),
    // so doublecheck that we can show the compset tab in that case.
    if (!params['activeTab'] && !availableTabs.includes(COMPSET)) {
      params['activeTab'] = availableTabs[0];
    }

    if (params['activeTab'] && !availableTabs.includes(params['activeTab'])) {
      params['activeTab'] = availableTabs[0];
    }
  }

  checkBenchmarkInsightDataSets(
    params: QueryParams & {
      primaryDataSet?: ComparisonSetWithoutOwn | null;
      secondaryDataSet?:
        | ComparisonSetWithoutOwn
        | DropdownComparisonSetsOptionNone;
    },
  ): void {
    const { allowedComparisonSets } = this.benchmarkInsightBeniService;

    // Primary data set separately
    if (
      !params['primaryDataSet'] ||
      (params['primaryDataSet'] &&
        !allowedComparisonSets.includes(params['primaryDataSet']))
    ) {
      if (params['secondaryDataSet'] === allowedComparisonSets[0]) {
        params['primaryDataSet'] = allowedComparisonSets[1] ?? null;
      } else {
        params['primaryDataSet'] = allowedComparisonSets[0] ?? null;
      }
    }

    // Secondary data set separately
    const availableSecondaryDataSetValues: (
      | ComparisonSetWithoutOwn
      | DropdownComparisonSetsOptionNone
    )[] = [...allowedComparisonSets, DROPDOWN_COMPARISON_SETS_OPTION_NONE];
    const availableSecondaryDataSetValuesWithoutPrimary =
      availableSecondaryDataSetValues.filter(
        (value) => value !== params['primaryDataSet'],
      );

    if (
      !params['secondaryDataSet'] ||
      (params['secondaryDataSet'] &&
        !availableSecondaryDataSetValuesWithoutPrimary.includes(
          params['secondaryDataSet'],
        ))
    ) {
      params['secondaryDataSet'] =
        availableSecondaryDataSetValuesWithoutPrimary[0];
    }

    // Primary & secondary data set combinations
    if (
      params['primaryDataSet'] &&
      params['secondaryDataSet'] &&
      params['primaryDataSet'] === params['secondaryDataSet']
    ) {
      params['secondaryDataSet'] = 'none';
    }
  }

  checkBenchmarkInsightDateAggregation(
    params: QueryParams & {
      dateAggregation?:
        | typeof DATE_AGGREGATION.DAY
        | typeof DATE_AGGREGATION.WEEK;
    },
  ): void {
    const { dateAggregation } = params;

    const benchmarkInsightDateAggregationOptions = [
      DATE_AGGREGATION.DAY,
      DATE_AGGREGATION.WEEK,
    ];

    if (
      dateAggregation === undefined ||
      !benchmarkInsightDateAggregationOptions.includes(dateAggregation)
    ) {
      params['dateAggregation'] = benchmarkInsightDateAggregationOptions[0];
    }
  }

  checkBenchmarkInsightDateRange(
    params: QueryParams & { fromDate?: string | null; toDate?: string | null },
  ): void {
    const { fromDate, toDate } = params;

    // Don't allow future dates if the user doesn't have access to future data.
    if (!this.access.hasBenchmarkInsightFutureData) {
      const { lastPastDate } = this.benchmarkInsightBeniService;
      if (
        dayjs(fromDate).isAfter(lastPastDate, 'day') ||
        dayjs(toDate).isAfter(lastPastDate, 'day')
      ) {
        params['fromDate'] = null;
        params['toDate'] = null;
      }
    }
  }

  checkBenchmarkInsightHiddenFutureCompsetData(
    params: QueryParams & {
      fromDate?: string;
      primaryDataSet?: ComparisonSetWithoutOwn | null;
    },
  ): void {
    const { primaryDataSet, fromDate } = params;
    const { lastPastDate } = this.benchmarkInsightBeniService;

    // `fromDate` can be `null`, in which case it's the default from our BENI controller (today).
    const fromDateWithFallback = this.hotelAndMarketService
      .toLocalTimeWithFallback(fromDate)
      .startOf('day');

    const isCompsetSelected: boolean =
      primaryDataSet === BENI_COMPARISON_SETS.COMPSET;
    const isFutureDateRange: boolean = dayjs(fromDateWithFallback).isAfter(
      lastPastDate,
      'day',
    );

    if (isCompsetSelected && isFutureDateRange) {
      if (params['selectedKpiHeroChart'] === KPIS.OCCUPANCY) {
        params['selectedAxisOption'] = KPI_INDEXOPTION.INDEX;
      }

      if (params['selectedKpiHeroChart'] === KPIS.ADR) {
        params['selectedAxisOption'] = KPI_INDEXOPTION.KPI_DIFFERENCE;
      }

      // Note: `selectedKpiLosChart` can be `undefined`, in that case it's the default (`KPIS.OCCUPANCY`).
      if (
        params['selectedKpiLosChart'] &&
        params['selectedKpiLosChart'] !== KPIS.OCCUPANCY
      ) {
        params['selectedLosSortOption'] =
          DROPDOWN_LOS_SORT_OPTIONS.MY_HIGHEST_PERFORMING;
      }
    }
  }

  checkBenchmarkInsightMarketCompositionActiveTab(
    params: QueryParams & { activeTab?: ComparisonSetWithoutOwn },
  ): void {
    const { activeTab } = params;

    const isMarketClass =
      activeTab === BENI_COMPARISON_SETS.MARKET_CLASS ||
      activeTab === BENI_COMPARISON_SETS.MARKET_COLLAPSED_CLASS;

    if (isMarketClass) {
      // BE may return all 3 classes
      // but FE only ever shows 2
      // we prefer market class over market collapsed class
      const correctMarketClass = BENI_COMPARISON_SETS.MARKET_CLASS;
      const incorrectClassSelected = activeTab !== correctMarketClass;
      if (incorrectClassSelected) {
        params['activeTab'] = correctMarketClass;
      }
    }

    if (
      params['activeTab'] &&
      !MARKET_COMPOSITION_TABS.includes(params['activeTab'])
    ) {
      const firstAvailableTab =
        MARKET_COMPOSITION_TABS.find((tab) =>
          this.benchmarkInsightBeniService.allowedComparisonSets.includes(tab),
        ) ?? DEFAULT_MARKET_COMPOSITION_TAB;

      params['activeTab'] = firstAvailableTab;
    }
  }

  checkBenchmarkInsightPickup(params: QueryParams & { pickup?: string }): void {
    const { pickup } = params;

    const supported = Object.values(BENI_PICKUP_DAYS).find((day) => {
      return pickup !== undefined && day === parseInt(pickup, 10);
    });

    // BENI Light package doesn't have access to pickup data
    if (!this.access.hasBenchmarkInsightPickupData) {
      params['pickup'] = undefined;
      return;
    }

    params['pickup'] = supported ? pickup : BENI_PICKUP_DAYS_DEFAULT.toString();
  }

  checkBenchmarkInsightReferencePeriod(params: QueryParams): void {
    const { referencePeriod } = params;

    const supported = Object.values(BENI_REFERENCE_PERIODS).find(
      (refPeriod) => {
        return referencePeriod !== undefined && refPeriod === referencePeriod;
      },
    );

    if (!this.access.hasBenchmarkInsightReferencePeriodData) {
      params['referencePeriod'] = undefined;
      return;
    }

    params['referencePeriod'] = supported
      ? referencePeriod
      : BENI_REFERENCE_PERIODS.STLY;
  }

  checkBenchmarkInsightSelectedAxisOption(
    params: QueryParams & {
      selectedAxisOption?: HeroChartAxisOption;
      selectedKpi?: BenchmarkKpi;
    },
  ): void {
    const { selectedKpi, selectedAxisOption } = params;

    const benchmarkInsightAxisOptions = Object.values(KPI_INDEXOPTION);

    if (
      selectedAxisOption &&
      !benchmarkInsightAxisOptions.includes(selectedAxisOption)
    ) {
      if (selectedKpi === KPIS.REVPAR) {
        params['selectedAxisOption'] = KPI_INDEXOPTION.INDEX;
      } else {
        params['selectedAxisOption'] = KPI_INDEXOPTION.KPI_DIFFERENCE;
      }
    }
  }

  checkBenchmarkInsightSelectedKpiHeroChart(
    params: QueryParams & { selectedKpiHeroChart?: BenchmarkKpi },
  ): void {
    const { selectedKpiHeroChart } = params;
    const benchmarkInsightKpis = Object.values(KPIS);

    if (
      selectedKpiHeroChart &&
      !benchmarkInsightKpis.includes(selectedKpiHeroChart)
    ) {
      params['selectedKpiHeroChart'] = benchmarkInsightKpis[0];
    }
  }

  checkBenchmarkInsightSelectedKpiLosChart(
    params: QueryParams & { selectedKpiLosChart?: BenchmarkKpi },
  ): void {
    const { selectedKpiLosChart } = params;

    const benchmarkInsightKpis = Object.values(KPIS);

    if (
      selectedKpiLosChart &&
      !benchmarkInsightKpis.includes(selectedKpiLosChart)
    ) {
      params['selectedKpiLosChart'] = benchmarkInsightKpis[0];
    }
  }

  checkBenchmarkInsightSelectedLosOccupancyView(
    params: QueryParams & {
      selectedLosOccupancyView?: OccupancyView;
    },
  ): void {
    const { selectedLosOccupancyView } = params;

    const benchmarkInsightLosViewOptions = Object.values(OCCUPANCY_VIEWS);

    if (
      selectedLosOccupancyView &&
      !benchmarkInsightLosViewOptions.includes(selectedLosOccupancyView)
    ) {
      params['selectedLosOccupancyView'] = OCCUPANCY_VIEWS.ACTUALS;
    }
  }

  checkBenchmarkInsightSelectedLosSortOption(
    params: QueryParams & { selectedLosSortOption?: DropdownLosSortOption },
  ): void {
    const { selectedLosSortOption } = params;

    const benchmarkInsightLosSortOptions = Object.values(
      DROPDOWN_LOS_SORT_OPTIONS,
    );

    if (selectedLosSortOption) {
      if (!benchmarkInsightLosSortOptions.includes(selectedLosSortOption)) {
        params['selectedLosSortOption'] =
          DROPDOWN_LOS_SORT_OPTIONS.MY_HIGHEST_PERFORMING;
      } else if (!this.benchmarkInsightFeatures.hasOwnPmsIntegration) {
        params['selectedLosSortOption'] =
          DROPDOWN_LOS_SORT_OPTIONS.PRIMARY_COMPARISON_SET_HIGHEST_PERFORMING;
      }
    }
  }

  checkBenchmarkInsightStayDateSectionView(
    params: QueryParams & { stayDateSectionView?: BeniStayDateSectionView },
  ): void {
    const { stayDateSectionView } = params;

    const benchmarkInsightstayDateSectionViews = Object.values(
      BENI_STAY_DATE_SECTION_VIEWS,
    );
    if (
      stayDateSectionView === undefined ||
      !benchmarkInsightstayDateSectionViews.includes(stayDateSectionView)
    ) {
      params['stayDateSectionView'] = BENI_STAY_DATE_SECTION_VIEWS.CHART;
    }
  }

  checkBestFlex(params: { rateParam?: string }, otaObj: Ota): void {
    const rateParam =
      params.rateParam ?? DEFAULT_QP_VALUES.application.rateParam;
    // Update Best Flex to Lowest if the OTA doesn't support it
    if (rateParam === RATE_PARAMS.BEST_FLEX && !otaObj.hasCancellationFilter) {
      params.rateParam = RATE_PARAMS.LOWEST;
    }
  }

  /** Query param validators
   * Fetching the QP values from the param object often involves one of these actions:
   * 1) Converting the value (which is still a String in the beforeModel hook) to the correct datatype
   * 2) Replacing the value with its default value when it is undefined (= QP not present in url)
   * */
  checkBrands(params: { brands?: string[] }): void {
    const { brands } = params;

    // Check if brands is undefined. Otherwise the default value will be passed which is an empty array
    if (brands && !Array.isArray(brands)) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const parsedBrands = JSON.parse(brands); // QP is passed as String
      // ToDo: refactor forEach to any?
      if (!Array.isArray(parsedBrands)) {
        params['brands'] = [];
      } else {
        parsedBrands.forEach((brand) => {
          if (
            this.hotelAndMarketService.market?.brands
              .map((mappedBrand) => mappedBrand.id)
              // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
              .indexOf(brand) === -1
          ) {
            params['brands'] = [];
          }
        });
      }
    }
  }

  checkCentralMappingBreakdown(
    params: QueryParams & { breakdown?: string },
  ): void {
    const { breakdown } = params;

    const sortedBreakdownNames = this.centralMapping.sortedBreakdowns.map(
      ({ breakdown }) => breakdown,
    );

    const isFtGatedBreakdownDisabled =
      breakdown === BREAKDOWNS.STAY_RESTRICTION_RATE_GROUP &&
      !this.featureToggle.hasFeatureToggle(
        FEATURE_TOGGLES.STAY_RESTRICTION_RATE_GROUP,
      );

    if (
      breakdown &&
      (!isMappingBreakdown(breakdown) ||
        !sortedBreakdownNames.includes(breakdown) ||
        isFtGatedBreakdownDisabled)
    ) {
      params['breakdown'] = sortedBreakdownNames[0];
    }
  }

  checkCentralMappingMenuView(params: QueryParams & { view?: string }): void {
    const { view } = params;

    if (
      view &&
      (!isCentralMappingMenuView(view) ||
        !this.access.hasBenchmarkInsightMappingTab)
    ) {
      params['view'] = CENTRAL_MAPPING_MENU_VIEWS.BI;
    }
  }

  checkCentralMappingPagination(
    params: QueryParams & { pageSize?: string | number },
  ): void {
    const { pageSize } = params;
    let pageSizeToUse = pageSize ?? CENTRAL_MAPPING_DEFAULT_PAGE_SIZE;

    if (
      !PAGE_NUMBER_OPTIONS.includes(
        pageSizeToUse as (typeof PAGE_NUMBER_OPTIONS)[number],
      )
    ) {
      pageSizeToUse = CENTRAL_MAPPING_DEFAULT_PAGE_SIZE;
    }

    if (pageSizeToUse === CENTRAL_MAPPING_DEFAULT_PAGE_SIZE) {
      pageSizeToUse =
        Number(
          this.localStorage.getItem(
            CENTRAL_MAPPING_LOCAL_STORAGE_KEY_USER_SELECTED_PAGE_SIZE,
          ),
        ) || CENTRAL_MAPPING_DEFAULT_PAGE_SIZE;
    }

    params['pageSize'] = pageSizeToUse;
  }

  checkCentralMappingUserIsStaffUser(params: QueryParams): void {
    if (!this.access.isStaffUser && params['managedBy']) {
      params['managedBy'] = undefined;
    }
  }

  checkCentralMappingValueType(params: QueryParams): void {
    const { breakdown: breakdownQP, valueType: valueTypeQP } = params;
    const { breakdowns } = this.centralMapping;
    const breakdown = breakdownQP ?? DEFAULT_BREAKDOWN;
    const valueType = valueTypeQP ?? DEFAULT_VALUE_TYPE;

    const breakdownRecord = breakdowns.find(
      (record) => record.breakdown === breakdown,
    );
    if (!breakdownRecord) {
      return;
    }

    const { valueTypes } = breakdownRecord;

    const valueTypeRecord = valueTypes.find(
      (record) => record.valueType === valueType,
    );

    if (!valueTypeRecord?.available) {
      params['valueType'] = valueTypes[0]?.valueType;
    }
  }

  checkCompare(
    params: QueryParams & {
      compareType?: CompareType;
      compareValue?: string;
      los?: string;
      maxPersons?: string;
      mealPlan?: string;
      membershipType?: string;
      platform?: string;
      roomType?: string;
    },
    otaObj: Ota,
    dayOrMonth: string,
  ): void {
    const { compareType, compareValue } = params;
    const los = params.los
      ? parseInt(params.los, 10)
      : DEFAULT_QP_VALUES.application.los;
    const mealPlan = params.mealPlan
      ? parseInt(params.mealPlan, 10)
      : DEFAULT_QP_VALUES.application.mealPlan;
    const maxPersons = parseInt(params.maxPersons as string, 10);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const membershipType = params.membershipType
      ? JSON.parse(params.membershipType)
      : DEFAULT_QP_VALUES.application.membershipType;
    const otaId = params['otaId'] ?? OTA_IDS.BOOKINGDOTCOM;
    const platform = params.platform
      ? parseInt(params.platform, 10)
      : DEFAULT_QP_VALUES.application.platform;
    const roomType = params.roomType ?? DEFAULT_QP_VALUES.application.roomType;
    const rateParam =
      params['rateParam'] ?? DEFAULT_QP_VALUES.application.rateParam;

    if (isComparingRates(compareType, compareValue)) {
      if (
        // Check if the current compareType is valid
        compareType === undefined ||
        !Object.values(COMPARETYPES).includes(compareType) ||
        // Check if the selected OTA can have rate changes for the current compareType
        (compareType === COMPARETYPES.DATE && !otaObj?.hasRatechange) ||
        // Hide ratechanges for months in the past (temporary)
        (!dayjs(dayOrMonth).isSame(
          this.hotelAndMarketService.getLocalTime(),
          'month',
        ) &&
          dayjs(dayOrMonth).isBefore(
            this.hotelAndMarketService.getLocalTime().startOf('month'),
          )) ||
        // Can't compare when "All Channels" is selected
        otaId === 'allchannels' ||
        // Can't compare identical rate params (e.g. Best Flex vs. Best Flex)
        (compareType === COMPARETYPES.RATE_PARAM &&
          compareValue === rateParam) ||
        // Can't compare membership types if OTA doesn't have member filter
        (compareType === COMPARETYPES.MEMBERSHIPTYPE &&
          !otaObj.hasMemberFilter) ||
        // Can't compare identical membership types (e.g. Public vs. Public)
        (compareType === COMPARETYPES.MEMBERSHIPTYPE &&
          compareValue === `${membershipType}`) ||
        // Public must be part of the membership compare
        (compareType === COMPARETYPES.MEMBERSHIPTYPE &&
          compareValue !== `${MEMBERSHIPTYPES.PUBLIC}` &&
          membershipType !== MEMBERSHIPTYPES.PUBLIC) ||
        // Can't compare identical mealtypes (e.g. Room Only vs. Room Only)
        (compareType === COMPARETYPES.MEALTYPE &&
          compareValue === `${mealPlan}`) ||
        // Can't compare to "Any Meal"
        (compareType === COMPARETYPES.MEALTYPE &&
          (`${mealPlan}` === '0' || compareValue === '0')) ||
        // Can't compare identical LOS (e.g. LOS1 vs. LOS1)
        (compareType === COMPARETYPES.LOS && compareValue === `${los}`) ||
        // Only possible value for los is '1'
        (compareType === COMPARETYPES.LOS && compareValue !== '1') ||
        // Can't compare identical number of guests (e.g. 1 Guest vs. 1 Guest)
        (compareType === COMPARETYPES.PERSONS &&
          compareValue === `${maxPersons}`) ||
        // Default number of guests must be part of the persons compare
        (compareType === COMPARETYPES.PERSONS &&
          compareValue !== `${this.hotelOrMarket.defaultPersons}` &&
          maxPersons !== this.hotelOrMarket.defaultPersons) ||
        // Can't compare identical platforms (e.g. Desktop vs. Desktop)
        (compareType === COMPARETYPES.PLATFORM &&
          compareValue === `${platform}`) ||
        // Can't compare 2 mobile platforms
        (compareType === COMPARETYPES.PLATFORM &&
          compareValue === `${PLATFORMS.PLATFORM_MOBILE_APP}` &&
          platform === PLATFORMS.PLATFORM_MOBILE_WEBSITE) ||
        // Can't compare 2 mobile platforms
        (compareType === COMPARETYPES.PLATFORM &&
          compareValue === `${PLATFORMS.PLATFORM_MOBILE_WEBSITE}` &&
          platform === PLATFORMS.PLATFORM_MOBILE_APP) ||
        // Can't compare platforms if OTA does not have mobile filter
        (compareType === COMPARETYPES.PLATFORM && !otaObj?.hasMobileFilter) ||
        // Can't compare platforms when 3rd Party is selected
        (compareType === COMPARETYPES.PLATFORM &&
          rateParam === RATE_PARAMS.THIRD_PARTY) ||
        // Can't compare against mobile app when it's not supported by the OTA
        (compareType === COMPARETYPES.PLATFORM &&
          compareValue === `${PLATFORMS.PLATFORM_MOBILE_APP}` &&
          !otaObj.supportedPlatforms.includes(PLATFORMS.PLATFORM_MOBILE_APP)) ||
        // Can't compare swly for dates in the past
        (compareType === COMPARETYPES.SWLY &&
          dayjs.utc(dayOrMonth).isBefore(dayjs.utc(), 'month')) ||
        // Can't compare identical roomtypes (e.g. Standard Room vs. Standard Room)
        (compareType === COMPARETYPES.ROOMTYPE &&
          compareValue === `${roomType}`) ||
        // Can't compare to "Any room"
        (compareType === COMPARETYPES.ROOMTYPE &&
          (`${roomType}` === 'all' || compareValue === 'all')) ||
        // Can only compare vs x days ago. The possible values for x are defined in the const file
        (compareType === COMPARETYPES.DATE &&
          AVAILABLE_RATECHANGES.indexOf(
            this.hotelAndMarketService
              .getLocalTime()
              .diff(
                this.hotelAndMarketService.toLocalTime(compareValue),
                'day',
              ),
          ) === -1)
      ) {
        params['compareType'] = '';
        params['compareValue'] = '';
      }
    }
  }

  checkCompetitorFilter(
    params: QueryParams & { competitors?: string[] },
  ): void {
    const { competitors } = params;

    let hasValidCompetitorFilter = false;
    const hasCompetitorsQP = competitors !== undefined;
    if (hasCompetitorsQP) {
      hasValidCompetitorFilter =
        Array.isArray(competitors) &&
        this.hotelAndMarketService.primaryAndSecondaryCompetitors.filter(
          (hotelinfo) => competitors.includes(hotelinfo.id as string),
        ).length > 0;
    }

    if (!hasCompetitorsQP || !hasValidCompetitorFilter) {
      // Default to the competitor IDs of the primary compset
      params['competitors'] = this.hotelAndMarketService.primaryCompetitors
        .map((hotelinfo) => hotelinfo.id)
        .filter((id) => id !== null);
    }
  }

  /**
   * Checks the compset query param. It needs to be in the possible compsets
   * The possible compsets are dependant on whether or not the hotel is matched or not
   * If the hotel is matched, simply return the available compsets
   * If the hotel is not matched, we need to ensure the compset has at least one competitor matched
   * Otherwise we would not have any data to show in the app
   * @param { string } compsetId      : the id of the compset in the query parameters
   * @param { string } selectedOTA    : the id of the OTA that is currently selected
   * @returns { array }               : return an array of new query params
   */
  checkCompset(
    params: QueryParams & { compsetId?: string; otaId?: string },
  ): void {
    const otaId = params['otaId'] ?? OTA_IDS.BOOKINGDOTCOM;
    const compsetId =
      params['compsetId'] ?? DEFAULT_QP_VALUES.application.compsetId;

    let possibleCompsetIds;
    // If the current hotel is matched, return all compsets
    if (this.hotelAndMarketService.isHotelMatched(otaId)) {
      possibleCompsetIds = this.hotelAndMarketService.availableCompsets.map(
        (compset) => compset.apiId,
      );
    } else {
      // Filter the available compsets based on whether they have any competitor that has the selected OTA matched
      possibleCompsetIds = this.hotelAndMarketService.availableCompsets
        .filter((compset) => {
          const compsetOtas = compset.competitors.reduce(
            // @ts-expect-error: No overload matches this call.
            (otas: string[], hotelinfo) => {
              return [
                ...otas,
                ...hotelinfo.otas.map((matchedOta) => matchedOta.id),
              ];
            },
            [],
          );

          // @ts-expect-error: Property 'indexOf' does not exist on type 'Hotelinfo'.
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call
          return compsetOtas.indexOf(otaId) !== -1;
        })
        .map((compset) => compset.apiId);
    }

    // If there is no possible compset, just stay with the default. This means no competitor has been matched with any OTA
    if (
      possibleCompsetIds.length &&
      possibleCompsetIds.indexOf(compsetId) === -1
    ) {
      params['compsetId'] = possibleCompsetIds[0];
    }
  }

  checkCompsetId(params: QueryParams): void {
    const activeCompsetId = this.hotelAndMarketService.activeCompset?.apiId;
    if (activeCompsetId && activeCompsetId !== params['compsetId']) {
      params['compsetId'] = this.hotelAndMarketService.activeCompset?.apiId;
    } else if (!activeCompsetId) {
      // Not sure if this case can ever occur, compsetId should always be initialized in setHotel()
      // Not sure if this case can ever occur, compsetId should always be initialized in setHotel()
      params['compsetId'] = DEFAULT_QP_VALUES.application.compsetId;
    }
  }

  checkCustomExtractDate(params: QueryParams): void {
    const { extractDate } = params;

    // Check custom extractDate
    if (extractDate && !this.access.isStaffUser) {
      delete params['extractDate'];
    }
  }

  // Parity does not abort the transition, so a deleted param keeps its
  // current value in the URL. Reset it to the default instead.
  checkCustomExtractDateForParity(params: QueryParams): void {
    const { extractDate } = params;

    if (extractDate && !this.access.isStaffUser) {
      params['extractDate'] = '';
    }
  }

  checkDayWithinRange(params: QueryParams & { day?: string | null }): void {
    const { day } = params;
    const { ratesDataStart, ratesDataEnd } = this.hotelAndMarketService;

    if (day && (ratesDataStart.isAfter(day) || ratesDataEnd.isBefore(day))) {
      params['day'] = null;
    }
  }

  checkDefaultDatesForParityInsight(params: QueryParams): void {
    const { extractDate, historicalExtractDate } = params;

    if (!extractDate) {
      params['extractDate'] = getTodayUtc();
    }

    if (!historicalExtractDate) {
      params['historicalExtractDate'] = getTodayUtc();
    }
  }

  // ToDo: in future this should be deprecated together with an overal datepicker (local) date refactor
  checkDemoFreezeDate(params: QueryParams): void {
    // Only revert to demo freeze date on initial load/refresh (otherwise actual current month won't be accessible)
    if (
      this.hotelAndMarketService.hotel?.revenueInsightDemoFreezeDate &&
      !this.hasVisitedRevenueInsight
    ) {
      // For demo properties: use the following default for the fromDate, toDate and refFromDate
      const { revenueInsightDemoFreezeDate } = this.hotelAndMarketService.hotel;
      const startDate = revenueInsightDemoFreezeDate.startOf('month');

      params['fromDate'] = startDate.format('YYYY-MM-DD'); // set to freezedate month
      params['toDate'] = revenueInsightDemoFreezeDate

        .endOf('month')
        .format('YYYY-MM-DD');
      params['refFromDate'] = startDate

        .subtract(WEEKS_IN_YEAR, 'week')
        .format('YYYY-MM-DD'); // set to SWLY
      params['reference'] = 'SWLY';
      params['stayDateFilter'] = ''; // clear this (could be in a different range)
      params['extractDate'] = revenueInsightDemoFreezeDate.format('YYYY-MM-DD'); // data seen as of freezedate
    }
  }

  checkDisableDataContinuity(params: QueryParams): void {
    const { disableDataContinuity } = params;

    if (
      disableDataContinuity !== undefined &&
      disableDataContinuity !== 'false' &&
      disableDataContinuity !== 'true'
    ) {
      params['disableDataContinuity'] = undefined;
    }

    // Only staff users can explicitly disable data continuity
    if (disableDataContinuity === 'true' && !this.access.isStaffUser) {
      params['disableDataContinuity'] = undefined;
    }
  }

  checkExpandedRowIds(
    params: QueryParams & { expandedRowIds?: string | string[] },
  ): void {
    const { expandedRowIds } = params;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const parsedExpandedRowIds =
      isEmpty(expandedRowIds) || expandedRowIds === undefined
        ? []
        : JSON.parse(expandedRowIds as string);

    // If the query param is not an array, reset to an empty array (e.g. when fiddled with or wrongly encoded)
    if (!Array.isArray(parsedExpandedRowIds)) {
      params['expandedRowIds'] = [];
    }
  }

  checkExtractDates(
    params: QueryParams & {
      dateAggregation?: DateAggregation;
      extractDate?: string;
      historicalExtractDate?: string;
    },
    firstExtractDate: string,
  ): void {
    const DATE_QPS = {
      EXTRACT_DATE: 'extractDate',
      HISTORICAL_EXTRACT_DATE: 'historicalExtractDate',
    } as const;

    const { dateAggregation } = params;

    const today = getTodayUtc();
    const lastPossibleExtractDate = getFormattedLastPossibleExtractDate();
    const firstAllowedHistoricalExtractDate =
      getFirstAllowedHistoricalExtractDate(firstExtractDate, dateAggregation);

    const LOWER_BOUNDS = {
      [DATE_QPS.EXTRACT_DATE]: firstExtractDate,
      [DATE_QPS.HISTORICAL_EXTRACT_DATE]:
        firstAllowedHistoricalExtractDate.format('YYYY-MM-DD'),
    } as const;

    [DATE_QPS.EXTRACT_DATE, DATE_QPS.HISTORICAL_EXTRACT_DATE].forEach(
      (dateQP) => {
        const dateMoment = dayjs(params[dateQP], 'YYYY-MM-DD');

        if (!dateMoment.isValid()) {
          params[dateQP] = today;
        } else if (dateMoment.isBefore(LOWER_BOUNDS[dateQP])) {
          params[dateQP] = LOWER_BOUNDS[dateQP];
        } else if (dateMoment.isAfter(today)) {
          params[dateQP] = today;
        } else if (dateMoment.isAfter(lastPossibleExtractDate, 'day')) {
          // Correct the date for DATE_AGGREGATION_WEEK if it's not today
          if (
            dateAggregation === DATE_AGGREGATION_WEEK &&
            !dateMoment.isSame(today, 'day')
          ) {
            params[dateQP] = today;
          }
        } else if (dateAggregation === DATE_AGGREGATION_MONTH) {
          // Snap to the Monday of the last week of the month (the monthly anchor date)
          const endOfMonth = dateMoment.endOf('month').startOf('isoWeek');
          const endOfMonthIsFuture = endOfMonth.isAfter(today);
          const currentDateIsEndOfMonth = dateMoment.isSame(endOfMonth);
          const currentDateIsLastPossible = dateMoment.isSame(
            lastPossibleExtractDate,
          );

          if (
            !endOfMonthIsFuture &&
            !currentDateIsEndOfMonth &&
            !currentDateIsLastPossible
          ) {
            params[dateQP] = endOfMonth.format('YYYY-MM-DD');
          } else if (endOfMonthIsFuture && !currentDateIsLastPossible) {
            params[dateQP] = lastPossibleExtractDate;
          }
        } else if (dateMoment.isoWeekday() !== 1) {
          params[dateQP] = dateMoment.isoWeekday(1).format('YYYY-MM-DD');
        }
      },
    );
  }

  checkFilterLimits(
    params: QueryParams & { segmentationFilters?: string },
  ): boolean {
    const { segmentationFilters } = params;

    // Check for users who mess with their filters
    if (segmentationFilters) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const parsedSegmentationFilters = JSON.parse(segmentationFilters);
      // Filter out subscription id, this one does not need a check as it is unlimited
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      const segmentations = Object.keys(parsedSegmentationFilters).filter(
        (segmentationId) => segmentationId !== 'subscription_id',
      );

      // Clear the filters if you've filtered on more than one breakdown
      if (segmentations.length > 1) {
        params['segmentationFilters'] = '';
      }

      // Clear the filters if you've filtered more segmentation items then possible
      // It's possible we don't have any segmentations anymore if hotels was the only filter
      if (
        segmentations.length &&
        segmentations[0] !== undefined &&
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        parsedSegmentationFilters[segmentations[0]].length >
          MAX_AMOUNT_OF_FILTERS_PER_BLOCK
      ) {
        params['segmentationFilters'] = '';
      }
    }
    return false;
  }

  checkFromDateType(params: QueryParams): void {
    const { fromDateType } = params;
    const hasDynamicShopping = this.featureToggle.hasFeatureToggle(
      FEATURE_TOGGLES.PARITY_DYNAMIC_SHOPPING,
    );

    if (!hasDynamicShopping || !isFromDateType(fromDateType)) {
      params['fromDateType'] =
        DEFAULT_QP_VALUES.market.parity.insight.fromDateType;
    }
  }

  checkHidePickupLY(
    params: QueryParams & { hidePickupLY?: string | boolean },
  ): void {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const hidePickupLY = params['hidePickupLY']
      ? JSON.parse(params['hidePickupLY'] as string)
      : DEFAULT_QP_VALUES.application.hidePickupLY;

    const { reference } = params;

    const isBudgetOrForecast =
      reference === 'budget' || reference === 'forecast';

    // Can only have the hidePickupLY for budget/forecast
    if (hidePickupLY && !isBudgetOrForecast) {
      params['hidePickupLY'] = false;
    } else if (
      !hidePickupLY &&
      isBudgetOrForecast &&
      this.hotelAndMarketService.hotel &&
      !this.access.hasFeature('revenueinsight.targets.show_pickup_ly')
    ) {
      params['hidePickupLY'] = true;
    }
  }

  checkHotelId(params: QueryParams, transitionFrom: string | undefined): void {
    const { hotelId, user } = params;

    // Apply the current hotel as default filter.
    // Only do this when transitioning from another route to the hotel access index. This prevents the following bugs:
    // - When viewing 'all hotels', switching to page 2 should not trigger applying the filter
    // - When deeplinking to the hotel access edit route, this redirect should not be triggered because it results in not opening the edit modal
    if (
      isEmpty(hotelId) &&
      transitionFrom !== 'hotel.settings.hotels.index' &&
      transitionFrom !== 'hotel.settings.hotels.edit' &&
      isEmpty(user) &&
      !this.hotelAndMarketService.market?.id &&
      !this.destination.destinationSubscription?.id
    ) {
      params['hotelId'] = this.hotelAndMarketService.hotel?.id;
    }
  }

  checkIfOnlyOneHotelSelected(
    params: QueryParams & { activeFilters?: string[] },
  ): void {
    const { activeFilters } = params;

    if (!activeFilters) {
      return;
    }

    const hotelCount = activeFilters.filter((filter) =>
      filter.startsWith(DIMENSIONS.HOTEL),
    ).length;

    if (hotelCount > 1) {
      const firstHotelIndex = activeFilters.findIndex((filter) =>
        filter.startsWith(DIMENSIONS.HOTEL),
      );

      params['activeFilters'] = activeFilters.filter(
        (filter, index) =>
          !filter.startsWith(DIMENSIONS.HOTEL) || index === firstHotelIndex,
      );
    }
  }

  checkKpiAndRevenueTabMatch(
    params: QueryParams & { kpi?: string; revenueTab?: string },
  ): void {
    const { kpi, revenueTab } = params;

    const isKpiARevenueTab =
      kpi !== undefined && (REVENUE_TABS as readonly string[]).includes(kpi);
    if (isKpiARevenueTab && revenueTab !== kpi) {
      params['revenueTab'] = kpi;
    }
  }

  checkKpiAndRoomsTabMatch(
    params: QueryParams & { kpi?: string; roomsTab?: string },
  ): void {
    const { kpi, roomsTab } = params;

    const isKpiDefaultOrARoomsTab =
      !kpi || (ROOMS_TABS as readonly string[]).includes(kpi);
    if (isKpiDefaultOrARoomsTab && roomsTab !== kpi) {
      params['roomsTab'] = kpi;
    }
  }

  // Snap an out-of-range leadtime (stale/edited URL) back to the default.
  checkLeadtime(params: QueryParams & { leadtime?: string | number }): void {
    if (params['leadtime'] === undefined) {
      return;
    }

    const leadtime = Number(params['leadtime']);

    if (!isPaceLeadtime(leadtime)) {
      params['leadtime'] = DEFAULT_PACE_LEADTIME;
    }
  }

  checkLos(
    params: QueryParams & { los?: string | number },
    otaObj: Ota,
    defaultLos: number[],
  ): void {
    const los = params['los']
      ? parseInt(params['los'] as string, 10)
      : DEFAULT_QP_VALUES.application.los;

    let validLosOptions = [...defaultLos];
    if (otaObj?.id === OTA_IDS.GDS) {
      validLosOptions = [];
      if (this.access.hasFeature('rates.gds_default_los')) {
        validLosOptions.push(defaultLos[0] as number); // Only take the first default LOS rates for GDS
      }
      if (this.access.hasFeature('rates.addons.gds_los_5_7')) {
        validLosOptions.push(5);
        validLosOptions.push(7);
      }
      if (this.access.hasFeature('rates.addons.gds_los_7_30')) {
        validLosOptions.push(7);
        validLosOptions.push(30);
      }
    }

    let maxLos = Math.max(...validLosOptions);
    if (this.access.hasRatesCustomLiveupdate && otaObj?.hasLiveShop) {
      maxLos = Math.max(maxLos, 14);
    }
    if (otaObj?.id === OTA_IDS.BOOKINGDOTCOM) {
      maxLos = Math.max(maxLos, MAX_LOS_BOOKING);
    } else if (otaObj?.id === OTA_IDS.EXPEDIA) {
      maxLos = Math.max(maxLos, MAX_LOS_EXPEDIA);
    }

    if (
      !Number.isInteger(los) ||
      los > maxLos ||
      los < 1 ||
      (!otaObj?.hasLosFilter && validLosOptions.indexOf(los) === -1)
    ) {
      params['los'] = validLosOptions[0];
    }
  }

  checkLosWithConfig(params: QueryParams, config: ParityConfig): void {
    const { los } = params;

    if (!isNumber(los) || ![DEFAULT_LOS, ...config.losValues].includes(los)) {
      params['los'] = DEFAULT_LOS;
    }
  }

  checkManagementTypes(
    params: QueryParams & { managementTypes?: string | string[] },
  ): void {
    const { managementTypes } = params;

    // QP is passed as String
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const parsedManagementTypes =
      isEmpty(managementTypes) || managementTypes === undefined
        ? []
        : JSON.parse(managementTypes as string);

    // If the query param is not an array, reset to an empty array (e.g. when fiddled with or wrongly encoded)
    if (!Array.isArray(parsedManagementTypes)) {
      params['managementTypes'] = [];
    }

    if (
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      parsedManagementTypes?.length > 0 &&
      Array.isArray(parsedManagementTypes) &&
      Array.isArray(this.hotelAndMarketService.market?.managementTypes)
    ) {
      parsedManagementTypes.forEach((managementType) => {
        if (
          this.hotelAndMarketService.market?.managementTypes
            .map((mappedManagementType) => mappedManagementType.id)
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            .indexOf(managementType) === -1
        ) {
          params['managementTypes'] = [];
        }
      });
    }
  }

  checkMarketInsightFullParameters(
    params: QueryParams & {
      expandedDetailSections?: string;
      focusedDetailSection?: string | null;
      selectedCalendarViewFilterIds?: string;
    },
  ): void {
    if (!this.access.hasFeature('app.market_insight.full')) {
      const {
        expandedDetailSections,
        focusedDetailSection,
        selectedCalendarViewFilterIds,
      } = params;
      if (
        expandedDetailSections &&
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        JSON.parse(expandedDetailSections).filter(
          (value: string) => value === 'price-aggressiveness',
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        ).length !== 0
      ) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        const newValues = JSON.parse(expandedDetailSections).filter(
          (val: string) => val !== 'price-aggressiveness',
        );

        params['expandedDetailSections'] =
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          newValues.length === 0 ? '' : JSON.stringify(newValues);
      }
      if (
        focusedDetailSection === 'price-aggressiveness' ||
        focusedDetailSection === 'competitor-list' ||
        focusedDetailSection === 'price-level-distribution'
      ) {
        params['focusedDetailSection'] = null;
      }
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const selectedCalendarViewFilterIdsParsed = selectedCalendarViewFilterIds
        ? JSON.parse(selectedCalendarViewFilterIds)
        : null;
      if (
        selectedCalendarViewFilterIdsParsed &&
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        selectedCalendarViewFilterIdsParsed.filter(
          (x: string) => x === 'price_high' || x === 'price_low',
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        ).length !== 0
      ) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        const filteredCalendar = selectedCalendarViewFilterIdsParsed.filter(
          (x: string) => x !== 'price_high' && x !== 'price_low',
        );

        params['selectedCalendarViewFilterIds'] =
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          filteredCalendar.length === 0 ? '' : JSON.stringify(filteredCalendar);
      }
    }
  }

  checkMarketSwitchForParityStrategy(
    params: QueryParams & { activeFilters?: string[] },
  ): void {
    const hasSwitchedMarket =
      this.lastActiveHotelOrMarketId !== null &&
      this.lastActiveHotelOrMarketId !==
        this.hotelAndMarketService.hotelOrMarket.id;

    if (hasSwitchedMarket) {
      params['activeFilters'] = [];
    }

    this.lastActiveHotelOrMarketId =
      this.hotelAndMarketService.hotelOrMarket.id;
  }

  checkMaxPersonsDefault(
    params: QueryParams & { maxPersons?: string | number },
  ): void {
    const { maxPersons } = params;

    // Initially maxPersons can be undefined, 0 (default value) or negative (if tampered with)
    if (!maxPersons || parseInt(maxPersons as string, 10) <= 0) {
      params['maxPersons'] = this.defaultPersons;
    }
  }

  checkMaxPersonsForMarketRates(
    params: QueryParams & {
      maxPersons?: string | number;
      scheduleReport?: string;
    },
  ): void {
    const maxPersons = parseInt(params['maxPersons'] as string, 10);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const scheduleReport = params['scheduleReport']
      ? JSON.parse(params['scheduleReport'])
      : DEFAULT_QP_VALUES.application.scheduleReport;

    if (
      !this.hotelAndMarketService.isMyHotels &&
      maxPersons !== this.defaultPersons &&
      scheduleReport
    ) {
      params['maxPersons'] = this.defaultPersons;
    }
  }

  checkMaxPersonsForParity(params: QueryParams): void {
    const { maxPersons } = params;

    if (
      !isNumber(maxPersons) ||
      !Number.isInteger(maxPersons) ||
      !AVAILABLE_PERSONS_PARITY.includes(maxPersons)
    ) {
      params['maxPersons'] = this.defaultPersons;
    }
  }

  checkMaxPersonsForRates(
    params: QueryParams & { maxPersons?: string | number },
    otaObj: Ota,
  ): void {
    const maxPersons = parseInt(params['maxPersons'] as string, 10);

    if (
      !Number.isInteger(maxPersons) ||
      (!otaObj?.hasPersonFilter && maxPersons !== this.defaultPersons) ||
      (otaObj?.id !== OTA_IDS.BOOKINGDOTCOM &&
        // @ts-expect-error: Argument of type 'string | null' is not assignable to parameter of type 'string'.
        OTA_IDS_WITH_4_GUESTS.indexOf(otaObj?.id) === -1 &&
        maxPersons > 2) ||
      (otaObj?.id !== OTA_IDS.BOOKINGDOTCOM && maxPersons > 4) ||
      maxPersons > 6
    ) {
      params['maxPersons'] = this.defaultPersons;
    }
  }

  checkMealplan(
    params: QueryParams & { mealPlan?: string | number },
    otaObj: Ota,
    defaultMealPlanValue: number = DEFAULT_QP_VALUES.application.mealPlan,
  ): void {
    const mealPlan = params['mealPlan']
      ? parseInt(params['mealPlan'] as string, 10)
      : defaultMealPlanValue;

    if (
      !Number.isInteger(mealPlan) ||
      mealPlan < 0 ||
      mealPlan > 5 ||
      (!otaObj?.hasMealplanFilter && mealPlan !== 0)
    ) {
      params['mealPlan'] = defaultMealPlanValue;
    }
  }

  checkMealPlanForParity(params: QueryParams): void {
    const { mealPlan } = params;

    if (
      !isMealType(mealPlan) ||
      !AVAILABLE_MEAL_PLANS_PARITY.some((plan) => plan === mealPlan)
    ) {
      params['mealPlan'] = MEALTYPES.ANY;
    }
  }

  checkMembershipType(
    params: QueryParams & { membershipType?: string | number },
    otaObj: Ota,
  ): void {
    const membershipType = params['membershipType']
      ? (JSON.parse(params['membershipType'] as string) as string)
      : DEFAULT_QP_VALUES.application.membershipType;

    if (!otaObj?.hasMemberFilter && membershipType) {
      params['membershipType'] = DEFAULT_QP_VALUES.application.membershipType;
    }
  }

  checkMIDates(
    params: QueryParams & {
      activeFilters?: string;
      extractDateIndex?: string;
      fromDate?: string | null;
      toDate?: string | null;
    },
  ): void {
    // Clear filters if the user tries to go in the past or too far in the future

    const { activeFilters, fromDate, toDate } = params;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const parsedActiveFilters = activeFilters && parseObject(activeFilters);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    const selectedDay = parsedActiveFilters?.[ACTIVE_FILTERS.DAY];
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const selectedRange =
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      parsedActiveFilters?.[ACTIVE_FILTERS.RANGE] &&
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
      parseDateRangeString(parsedActiveFilters[ACTIVE_FILTERS.RANGE]);

    const startOfData = this.hotelAndMarketService.getLocalTime();
    const endOfData = this.hotelAndMarketService
      .getLocalTime()
      .add(MAX_DAYS_IN_FUTURE, 'day');

    const fromDateMoment = fromDate
      ? this.hotelAndMarketService.toLocalTime(fromDate)
      : null;
    const toDateMoment = toDate
      ? this.hotelAndMarketService.toLocalTime(toDate)
      : null;

    if (fromDateMoment && fromDateMoment.isBefore(startOfData, 'day')) {
      params['fromDate'] = startOfData.format('YYYY-MM-DD');
    }

    if (toDateMoment && toDateMoment.isAfter(endOfData, 'day')) {
      params['toDate'] = endOfData.format('YYYY-MM-DD');
    }

    if (fromDateMoment && toDateMoment) {
      if (
        fromDateMoment.isAfter(toDateMoment) ||
        fromDateMoment.isAfter(endOfData, 'day') ||
        toDateMoment.isBefore(startOfData, 'day')
      ) {
        params['fromDate'] = null;
        params['toDate'] = null;
      }
    }

    if (selectedDay) {
      const selectedDayMoment =
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        this.hotelAndMarketService.toLocalTime(selectedDay);
      if (
        selectedDayMoment.isBefore(startOfData, 'day') ||
        selectedDayMoment.isAfter(endOfData, 'day')
      ) {
        params['activeFilters'] = '';
        params['extractDateIndex'] = '0';
      }
    }

    if (selectedRange) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const { start, end } = selectedRange;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      const startMoment = this.hotelAndMarketService.toLocalTime(start);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      const endMoment = this.hotelAndMarketService.toLocalTime(end);
      if (
        startMoment.isBefore(startOfData, 'day') ||
        endMoment.isAfter(endOfData, 'day')
      ) {
        params['activeFilters'] = '';
      }
    }
  }

  checkMonthDefault(params: QueryParams): void {
    const { month } = params;

    if (!month) {
      params['month'] = this.hotelAndMarketService
        .getLocalTime()
        .format('YYYY-MM');
    }
  }

  checkMonthInPast(params: QueryParams & { month?: string }): void {
    const { month } = params;

    if (
      month &&
      this.hotelAndMarketService
        .toLocalTime(month)
        .endOf('month')
        .isBefore(this.hotelAndMarketService.getLocalTime())
    ) {
      params['month'] = this.hotelAndMarketService
        .getLocalTime()
        .format('YYYY-MM');
    }
  }

  checkMonthInPastPricingAssistant(
    params: QueryParams & { month?: string; view?: string },
  ): void {
    const { view, month } = params;

    if (view === PRIMA_VIEWS.CALENDAR) {
      this.checkMonthInPast(params);
    } else if (view === PRIMA_VIEWS.TABLE) {
      const originalMonth = this.hotelAndMarketService
        .toLocalTime(month)
        .endOf('month');
      const oneMonthAgo = this.hotelAndMarketService
        .getLocalTime()
        .subtract(1, 'month');

      // Edge case: In table view, we can select a week that is part in current month and part in previous month
      // In this case, both month and week are set in previous month which is a valid selection
      // Therefore, we allow month to go at least one month in the past, rest remains the same
      if (originalMonth.isBefore(oneMonthAgo, 'month')) {
        this.checkMonthInPast(params);
      }
    }
  }

  checkMonthWithinMaxAllowedPricingAssistantRange(
    params: QueryParams & { month?: string },
  ): void {
    // We only support seeing 12 months in the future
    const { month } = params;
    const lastSupportedMonth = this.hotelAndMarketService
      .getLocalTime()
      .add(MAX_ALLOWED_MONTHS_IN_FUTURE, 'month');

    if (lastSupportedMonth.isBefore(month, 'month')) {
      params['month'] = this.hotelAndMarketService
        .getLocalTime()
        .format('YYYY-MM');
    }
  }

  checkMonthWithinRange(params: QueryParams & { month?: string }): void {
    const { month } = params;
    const { ratesDataStart, ratesDataEnd } = this.hotelAndMarketService;

    if (ratesDataStart.isAfter(month) || ratesDataEnd.isBefore(month)) {
      params['month'] = this.hotelAndMarketService
        .getLocalTime()
        .format('YYYY-MM');
    }
  }

  checkNetRevenueComparisonFromDate(validatedQueryParams: {
    comparisonFromDate?: string | null;
  }): void {
    const { comparisonFromDate } = validatedQueryParams;

    // A null comparisonFromDate lets model() re-derive it via defaultComparisonFromDate.
    if (
      comparisonFromDate != null &&
      !dayjs(comparisonFromDate, DATE_FORMAT, true).isValid()
    ) {
      validatedQueryParams['comparisonFromDate'] = null;
    }
  }

  checkNetRevenueComparisonMode(validatedQueryParams: {
    comparisonMode?: string | null;
  }): void {
    const { comparisonMode } = validatedQueryParams;

    if (
      comparisonMode != null &&
      !(Object.values(COMPARISON_MODES) as string[]).includes(comparisonMode)
    ) {
      validatedQueryParams['comparisonMode'] = COMPARISON_MODES.SWLY;
    }
  }

  checkNetRevenueDates(validatedQueryParams: {
    fromDate?: string | null;
    toDate?: string | null;
  }): void {
    if (
      validatedQueryParams.fromDate != null &&
      !dayjs(validatedQueryParams.fromDate, DATE_FORMAT, true).isValid()
    ) {
      validatedQueryParams['fromDate'] = null;
    }

    if (
      validatedQueryParams.toDate != null &&
      !dayjs(validatedQueryParams.toDate, DATE_FORMAT, true).isValid()
    ) {
      validatedQueryParams['toDate'] = null;
    }

    const { fromDate, toDate } = validatedQueryParams;

    // The model() hook defaults fromDate/toDate independently, so a lone date is
    // valid; only an inverted range needs resetting.
    if (fromDate != null && toDate != null && dayjs(fromDate).isAfter(toDate)) {
      validatedQueryParams['fromDate'] = null;
      validatedQueryParams['toDate'] = null;
    }
  }

  checkOtaGroup(params: QueryParams): void {
    const { OTAGroup } = params;

    if (!this.access.hasLimitedParityView && OTAGroup === 'all') {
      params['OTAGroup'] = 'major';
    }
  }

  checkOtaSelectionForMarketRates(params: QueryParams & { otaId?: string }): {
    otaObj: Ota;
    reason: string | undefined;
  } {
    let otaId = params['otaId'] ?? OTA_IDS.BOOKINGDOTCOM;
    let otaObj = this.getOtaObj(otaId) as Ota;
    let reason;

    const validMarketRatesOtas = this.hotelOrMarket.OTAsRates;
    const connectedMarketRatesOtas = validMarketRatesOtas.filter(
      (ota) => ota.isConnectedOrBrandDotCom,
    );
    if (!connectedMarketRatesOtas?.find((ota) => ota.id === otaId)) {
      // @ts-expect-error: Type 'string | null | undefined' is not assignable to type 'string | undefined'.
      otaId = connectedMarketRatesOtas?.[0]?.id;
      if (otaId) {
        otaObj = this.store.peekRecord<Ota>('ota', otaId) as Ota;
        params['otaId'] = otaId;
      } else {
        reason = validMarketRatesOtas.length
          ? 'error.no_connected_otas'
          : 'error.no_otas_configured'; // If the valid rates otas array contains OTAs it means those are disconnected, show different error
      }
    }

    return {
      otaObj,
      reason,
    };
  }

  checkOtaSelectionForRates(
    validatedQueryParams: QueryParams & { otaId?: string },
  ): { otaObj: Ota; reason: string | undefined } {
    let otaId = validatedQueryParams['otaId'] ?? OTA_IDS.BOOKINGDOTCOM;
    let otaObj = this.getOtaObj(otaId) as Ota;
    let reason;

    // We need to do some extra checks here (similar to the ones in the ota-selections component). There we filter OTAs that are not matched for the own hotel or at least one of it's competitors.
    const validHotelRatesOtas = this.hotelOrMarket.OTAsRates.filter((ota) => {
      // Check if the own hotel has the ota matched
      if (this.hotelAndMarketService.isHotelMatched(ota.id)) {
        return true;
      }
      // Check if any competitor has the OTA matched
      // We do this by looping over every competitor
      // We create an array of ota ids, these are the otas that any competitor in this compset has a matching for
      // That way we can ensure that there is data for a certain ota
      const competitorOtas =
        this.hotelAndMarketService.primaryAndSecondaryCompetitors.reduce(
          // @ts-expect-error: No overload matches this call.
          (otas: string[], hotelinfo) => {
            // The addObjects method ensures we have unique values in the array
            return [
              ...otas,
              ...hotelinfo.otas.map((matchedOta) => matchedOta.id),
            ];
          },
          [],
        );

      // Check if the ota is matched for any competitor
      // @ts-expect-error Property 'indexOf' does not exist on type 'Hotelinfo'.
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      return competitorOtas.indexOf(ota.id) >= 0;
    });

    let connectedRatesOtas = validHotelRatesOtas.filter(
      (ota) => ota.isConnectedOrBrandDotCom,
    );
    // Filter out All Channels if it's the only connected OTA
    if (connectedRatesOtas.length === 1) {
      connectedRatesOtas = connectedRatesOtas.filter(
        (ota) => ota.id !== 'allchannels',
      );
    }

    if (!connectedRatesOtas.find((ota) => ota.id === otaId)) {
      // @ts-expect-error: Type 'string | null | undefined' is not assignable to type 'string | undefined'.
      otaId = connectedRatesOtas?.[0]?.id;
      if (otaId) {
        otaObj = this.store.peekRecord<Ota>('ota', otaId) as Ota;
        validatedQueryParams['otaId'] = otaId;
      } else {
        reason = validHotelRatesOtas.length
          ? 'error.no_connected_otas'
          : 'error.no_otas_configured'; // If the valid rates otas array contains OTAs it means those are disconnected, show different error
      }
    }

    return {
      otaObj,
      reason,
    };
  }

  checkOtasForRanking(params: QueryParams): void {
    const otaId = params['otaId'] ?? OTA_IDS.BOOKINGDOTCOM;

    if (!this.hotelOrMarket?.OTAsRanking.find((ota) => ota.id === otaId)) {
      params['otaId'] = this.hotelOrMarket?.OTAsRanking?.[0]?.id;
    }
  }

  checkOverviewDashboardDates(
    params: QueryParams & {
      activeFilters?: string;
      fromDate?: string | null;
      toDate?: string | null;
    },
  ): void {
    // Clear filters if the user tries to go in the past or too far in the future
    const { fromDate, toDate, activeFilters } = params;

    // Note: startOfData used to be the current day (forward looking data only)
    // Users will now be able to select the full current month because users wanted to see that data for certain KPIs such as OTB/ADR/Revenue)
    // -> some past days are possible and that's why this check has been updated here
    const startOfData = this.hotelAndMarketService
      .getLocalTime()
      .startOf('month');
    const endOfData = this.hotelAndMarketService.getLocalTime().add(1, 'year');

    // In case there is explicit fromDate in the URL, we set the fromDateMoment to the fromDate
    // In case there is no explicit fromDate in the URL, we set the fromDateMoment to today
    const fromDateMoment =
      this.hotelAndMarketService.toLocalTimeWithFallback(fromDate);
    const toDateMoment = toDate
      ? this.hotelAndMarketService.toLocalTime(toDate)
      : this.hotelAndMarketService
          .getLocalTime()
          .add(DEFAULT_DAYS_SHOWN_IN_OD, 'days');

    // fromDate in the URL should not be before startOfData (start of the current month)
    // fromDate in the URL should not be before toDate
    // toDate in the URL should not be after the endOfData (today + 1 year)
    // toDate in the URL should not be after fromDate
    if (
      fromDateMoment.isBefore(startOfData, 'day') ||
      fromDateMoment.isAfter(toDateMoment, 'day') ||
      toDateMoment.isBefore(fromDateMoment, 'day') ||
      toDateMoment.isAfter(endOfData, 'day')
    ) {
      params['fromDate'] = null;
      params['toDate'] = null;
    }

    // Extra sanity check to see if the day drilldown filter value has a valid value
    // The drilled down day is invalid if it is not in the range of the fromDateMoment and toDateMoment - Clear the active filters in that case
    if (activeFilters) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const parsedActiveFilters = JSON.parse(activeFilters);

      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (parsedActiveFilters[OD_FB_FILTERS.DAY]) {
        if (
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
          dayjs(parsedActiveFilters[OD_FB_FILTERS.DAY]).isBefore(
            fromDateMoment,
            'day',
          ) ||
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
          dayjs(parsedActiveFilters[OD_FB_FILTERS.DAY]).isAfter(
            toDateMoment,
            'day',
          )
        ) {
          params['activeFilters'] = '';
        }
      }
    }
  }

  checkParityAnalyticsView(params: QueryParams): void {
    const { parityAnalyticsView, los } = params;

    if (
      parityAnalyticsView === false &&
      this.access.hasParityAnalyticsStaff &&
      !this.access.hasFeature('app.parity')
    ) {
      params['parityAnalyticsView'] = 'true';
      // Note: Dirty hack to get los in here, we need to rework the selections to retain the correct QPs
      // It does not keep them now, so the tab crashes if los is not in there
      // Note: Dirty hack to get los in here, we need to rework the selections to retain the correct QPs
      // It does not keep them now, so the tab crashes if los is not in there
      params['los'] = los;
    }

    if (parityAnalyticsView) {
      if (los !== this.hotelOrMarket?.defaultLosParityAnalytics?.[0]) {
        params['los'] = this.hotelOrMarket?.defaultLosParityAnalytics?.[0];
        // Note: Dirty hack to get parityAnalyticsView in here, we need to rework the selections to retain the correct QPs
        // It does not keep them now, so the tab crashes if parityAnalyticsView is not in there
        // Note: Dirty hack to get parityAnalyticsView in here, we need to rework the selections to retain the correct QPs
        // It does not keep them now, so the tab crashes if parityAnalyticsView is not in there

        params['parityAnalyticsView'] = 'true';
      }
    } else {
      // Check defaultLosParityAnalytics in case of limited parity view
      const defaultLos = this.access.hasLimitedParityView
        ? this.hotelOrMarket.defaultLosParityAnalytics
        : this.hotelOrMarket.defaultLosParity;
      const maxLos = Math.max(...defaultLos.concat([14]));
      if (
        !isNumber(los) ||
        !Number.isInteger(los) ||
        los > maxLos ||
        los < 1 ||
        (defaultLos.indexOf(los) === -1 &&
          !this.access.hasParityCustomLiveupdate)
      ) {
        params['los'] = defaultLos[0];
      }
    }
  }

  checkPlatform(
    params: QueryParams & { platform?: string | number },
    otaObj: Ota,
  ): void {
    const platform = params['platform']
      ? parseInt(params['platform'] as string, 10)
      : DEFAULT_QP_VALUES.application.platform;

    const isMissingFeatureFlagForMobile =
      !this.access.hasFeature('rates.mobile') &&
      platform !== DEFAULT_QP_VALUES.application.platform;
    const hasNoMobileFilterForOta =
      !otaObj.hasMobileFilter &&
      platform !== DEFAULT_QP_VALUES.application.platform;

    if (isMissingFeatureFlagForMobile || hasNoMobileFilterForOta) {
      params['platform'] = DEFAULT_QP_VALUES.application.platform;
    } else if (otaObj.mappedSupportedPlatforms.indexOf(platform) === -1) {
      params['platform'] = otaObj.mappedSupportedPlatforms[0];
    }
  }

  checkPlatformForParity(params: QueryParams): void {
    const { platform } = params;

    if (
      !isPlatform(platform) ||
      !(AVAILABLE_PLATFORMS_PARITY as readonly Platform[]).includes(platform)
    ) {
      params['platform'] = PLATFORMS.PLATFORM_UNSPECIFIED;
    }
  }

  checkPmsMappingActiveTab(
    params: QueryParams & {
      activeTab?: (typeof PMS_MAPPING_TABS_BENCHMARK)[number];
    },
  ): void {
    const { activeTab } = params;

    // Go to the default when on an invalid tab
    const mappingTabs = PMS_MAPPING_TABS_BENCHMARK;
    if (activeTab === undefined || !mappingTabs.includes(activeTab)) {
      params['activeTab'] = mappingTabs[0];
    }
  }

  checkPosWithConfig(params: QueryParams, config: ParityConfig): void {
    const { pos } = params;

    const isCountryCodeOrDefault =
      pos === DEFAULT_POS || Countries.isCountryCode(pos);

    if (
      !isCountryCodeOrDefault ||
      ![DEFAULT_POS, ...config.posValues].includes(pos)
    ) {
      params['pos'] = DEFAULT_POS;
    }
  }

  checkPricingAssistantDynamicRateTableRateCodeQueryParam(
    params: QueryParams & { rateCode?: string },
  ): void {
    const hotel = this.hotelAndMarketService.hotel as Hotel;
    const defaultRateCode = hotel.primaRateCodes[0]?.id;
    const possibleRateCode = params['rateCode'] ?? defaultRateCode;

    const validRateCodes = hotel.primaRateCodes.map(
      ({ id: rateCode }) => rateCode,
    );

    const matchedRateCode = validRateCodes.find(
      (rateCode) => rateCode === possibleRateCode,
    );

    params['rateCode'] = matchedRateCode ? possibleRateCode : defaultRateCode;
  }

  checkPricingAssistantSelectionsQueryParams(params: QueryParams): void {
    // Set default pricing selections filter related QPs
    const hotel = this.hotelAndMarketService.hotel as Hotel;
    const { view } = params;

    params['otaId'] = hotel.paRatePreference.channel;
    params['los'] = hotel.paRatePreference.los.toString();

    // The parameters below have hardcoded values. They are initialised in the controller with that value
    // We set them here again as extra validation, so the user can never temper with parameters
    params['platform'] =
      DEFAULT_QP_VALUES.hotel['pricing-manager'].platform.toString();
    params['compsetId'] = DEFAULT_QP_VALUES.hotel['pricing-manager'].compsetId;
    params['membershipType'] =
      DEFAULT_QP_VALUES.hotel['pricing-manager'].membershipType.toString();

    if (view === PRIMA_VIEWS.TABLE) {
      // We show all room types in the table view, so we don't need to filter by room type
      params['roomType'] = undefined;
      params['maxPersons'] = hotel.paRatePreference.persons.toString();

      // For DRP users, we only have dynamic rate param filter in the table view
      if (this.access.hasPricingAssistantDynamicRatePricingFeature) {
        this.checkPricingAssistantDynamicRateTableRateCodeQueryParam(params);

        params['mealPlan'] ??= hotel.paRatePreference.mealtype.toString();
        params['rateParam'] ??= hotel.paRatePreference.rate_type;
        params['platform'] =
          DEFAULT_QP_VALUES.hotel['pricing-manager'][
            'pricing-manager-dynamic-rate'
          ].platform.toString();

        const { otaObj } = this.checkOtaSelectionForRates(params);
        this.checkRateParamForRates(params, otaObj);
        this.checkMealplan(params, otaObj, hotel.paRatePreference.mealtype);
      } else {
        params['rateParam'] = hotel.paRatePreference.rate_type;
        params['mealPlan'] = hotel.paRatePreference.mealtype.toString();
        params['rateCode'] = undefined;
      }
    } else if (view === PRIMA_VIEWS.CALENDAR) {
      params['rateCode'] = undefined;
      // For ORP users, we only have dynamic room type and guests filter in the calendar view
      if (this.access.hasPricingAssistantOpenPricingFeature) {
        params['roomType'] =
          params['roomType'] ?? hotel.paRatePreference.room_type.id.toString();
        params['maxPersons'] =
          params['maxPersons'] ?? hotel.paRatePreference.persons.toString();
      } else {
        params['roomType'] = hotel.paRatePreference.room_type.id.toString();
        params['maxPersons'] = hotel.paRatePreference.persons.toString();
      }
      params['rateParam'] = hotel.paRatePreference.rate_type;
      params['mealPlan'] = hotel.paRatePreference.mealtype.toString();
    }
  }

  checkPricingAssistantViewQueryParam(
    params: QueryParams & { view?: string },
  ): void {
    const { view } = params;
    const {
      hasPricingAssistantDynamicRatePricingFeature,
      hasPricingAssistantOpenPricingFeature,
    } = this.access;
    const isValidPrimaView = view && Object.values(PRIMA_VIEWS).includes(view);

    if (view === PRIMA_VIEWS.TABLE && !hasPricingAssistantOpenPricingFeature) {
      params['view'] = PRIMA_VIEWS.CALENDAR;
      return;
    }

    if (isValidPrimaView) {
      return;
    }

    params['view'] = hasPricingAssistantDynamicRatePricingFeature
      ? PRIMA_VIEWS.TABLE
      : PRIMA_VIEWS.CALENDAR;
  }

  /**
   * Validates the `profile` query param against the exchange-rate profiles that
   * are already cached. Non-failing: it never awaits a fetch, so it can't block
   * the transition.
   */
  checkProfile(params: QueryParams): void {
    const { profile } = params;
    if (!profile) {
      return;
    }

    const cachedProfiles =
      this.currencyConversion.getExchangeRatesProfilesTask.last?.value;
    if (!cachedProfiles) {
      return;
    }

    const matchedProfile = cachedProfiles.find((p) => p.id === profile);
    if (!matchedProfile || !matchedProfile.isValid) {
      params['profile'] = null;
    }
  }

  checkRateParamForMarketRates(params: QueryParams, otaObj: Ota): void {
    // 3rd Party is not allowed in Market Rates
    if (params['rateParam'] === RATE_PARAMS.THIRD_PARTY) {
      params['rateParam'] = RATE_PARAMS.BEST_FLEX;
    }

    if (
      params['rateParam'] === RATE_PARAMS.FLEXIBLE &&
      !otaObj.hasFlexibleFilter
    ) {
      params['rateParam'] = RATE_PARAMS.BEST_FLEX;
    }
  }

  checkRateParamForParity(params: QueryParams): void {
    const { rateParam } = params;

    if (
      rateParam === RATE_PARAMS.THIRD_PARTY &&
      !(this.hotelAndMarketService.hotel?.OTAsParity ?? []).some(
        (ota) => ota.hasThirdPartyFilter,
      )
    ) {
      params['rateParam'] = RATE_PARAMS.BEST_FLEX;
    }

    // Lowest Flex is not allowed in Parity tab
    if (!isRateParam(rateParam) || rateParam === RATE_PARAMS.FLEXIBLE) {
      params['rateParam'] = RATE_PARAMS.BEST_FLEX;
    }
  }

  checkRateParamForRates(
    params: QueryParams & { platform?: string },
    otaObj: Ota,
  ): void {
    const platform = params['platform']
      ? parseInt(params['platform'], 10)
      : DEFAULT_QP_VALUES.application.platform;
    const isMobileRates =
      platform === PLATFORMS.PLATFORM_MOBILE_WEBSITE ||
      platform === PLATFORMS.PLATFORM_MOBILE_APP;

    if (
      params['rateParam'] === RATE_PARAMS.THIRD_PARTY &&
      (!otaObj.hasThirdPartyFilter ||
        (isMobileRates && !otaObj.isMobileThirdPartyRatesSupported))
    ) {
      params['rateParam'] = RATE_PARAMS.BEST_FLEX;
    }

    if (
      params['rateParam'] === RATE_PARAMS.FLEXIBLE &&
      !otaObj.hasFlexibleFilter
    ) {
      params['rateParam'] = RATE_PARAMS.BEST_FLEX;
    }
  }

  checkRateType(params: QueryParams): void {
    const { rateType } = params;

    if (!isRateType(rateType)) {
      params['rateType'] = DEFAULT_QP_VALUES.market.parity.insight.rateType;
    }
  }

  checkReference(params: QueryParams): void {
    const { selectedView, reference } = params;
    if (
      selectedView === 'cancellations' &&
      (reference === 'budget' || reference === 'forecast')
    ) {
      params['reference'] = DEFAULT_QP_VALUES.application.reference;
    }
  }

  checkRevenueInsightSelectedView(params: QueryParams): void {
    const { selectedView, cancellationsView } = params;

    if (
      !this.access.hasFeature('revenueinsight.cancellations') &&
      selectedView === VIEWS.CANCELLATIONS
    ) {
      params['selectedView'] =
        DEFAULT_QP_VALUES.hotel['revenue-insight'].selectedView;
    }

    // Reset cancellationsView if selectedView is not cancellations
    const defaultCancellationsView =
      DEFAULT_QP_VALUES.hotel['revenue-insight'].cancellationsView;
    if (
      selectedView === VIEWS.NET &&
      cancellationsView !== defaultCancellationsView
    ) {
      params['cancellationsView'] = defaultCancellationsView;
    }
  }

  checkRevenueTab(params: QueryParams & { revenueTab?: string }): void {
    const { revenueTab } = params;

    if (
      revenueTab &&
      !(REVENUE_TABS as readonly string[]).includes(revenueTab)
    ) {
      params['revenueTab'] =
        DEFAULT_QP_VALUES.hotel['revenue-insight'].revenueTab;
    }
  }

  checkRIDates(
    params: QueryParams & {
      fromDate?: string;
      refFromDate?: string;
      stayDateFilter?: string;
    },
  ): void {
    const { fromDate, refFromDate, stayDateFilter } = params;
    const { dateRangeStart } = getDateRange(
      fromDate,
      refFromDate,
      this.hotelAndMarketService,
      stayDateFilter,
    );
    const fromDateMoment = dayjs(dateRangeStart);
    const refFromDateMoment = dayjs(
      refFromDate ||
        dayjs().startOf('month').subtract(52, 'week').format('YYYY-MM-DD'),
    );
    const reference =
      params['reference'] || DEFAULT_QP_VALUES.application.reference;
    const { dataContinuityFirstCompleteStayDateWithDefault } = this
      .hotelAndMarketService.hotel as Hotel;

    // Check for dates
    if (
      reference === DEFAULT_QP_VALUES.application.reference &&
      !fromDateMoment.subtract(52, 'week').isSame(refFromDateMoment)
    ) {
      const correctRefFromDate = fromDateMoment.subtract(52, 'week');
      params['refFromDate'] = correctRefFromDate.format('YYYY-MM-DD');
    }

    if (
      reference === 'SDLY' &&
      !fromDateMoment.subtract(1, 'year').isSame(refFromDateMoment)
    ) {
      const correctRefFromDate = fromDateMoment.subtract(1, 'year');
      params['refFromDate'] = correctRefFromDate.format('YYYY-MM-DD');
    }

    if (
      fromDateMoment.isBefore(dataContinuityFirstCompleteStayDateWithDefault)
    ) {
      let startMoment = dayjs().startOf('month');
      if (
        startMoment.isBefore(
          dataContinuityFirstCompleteStayDateWithDefault,
          'day',
        )
      ) {
        startMoment = dataContinuityFirstCompleteStayDateWithDefault;
      }

      params['fromDate'] = startMoment.format('YYYY-MM-DD');
      params['toDate'] = startMoment.endOf('month').format('YYYY-MM-DD');
      params['refFromDate'] = startMoment

        .subtract(52, 'week')
        .format('YYYY-MM-DD');
      params['reference'] = DEFAULT_QP_VALUES.application.reference;
    }
  }

  checkRoomsTab(params: QueryParams & { roomsTab?: string }): void {
    const { roomsTab } = params;

    if (roomsTab && !(ROOMS_TABS as readonly string[]).includes(roomsTab)) {
      params['roomsTab'] = DEFAULT_QP_VALUES.hotel['revenue-insight'].roomsTab;
    }
  }

  checkRoomType(params: QueryParams, otaObj: Ota): void {
    const roomType =
      params['roomType'] ?? DEFAULT_QP_VALUES.application.roomType;

    if (
      !otaObj?.hasRoomtypeFilter &&
      roomType !== DEFAULT_QP_VALUES.application.roomType
    ) {
      params['roomType'] = DEFAULT_QP_VALUES.application.roomType;
    }
  }

  checkSegmentationFilters(
    params: QueryParams & { segmentationFilters?: string },
  ): void {
    const { reference, segmentationFilters } = params;

    if (!segmentationFilters) {
      return;
    }

    let parsedSegmentationFilters: Record<string, unknown>;
    try {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      parsedSegmentationFilters = JSON.parse(segmentationFilters);
    } catch {
      // Clear filters that aren't valid JSON, e.g. a malformed query param
      params['segmentationFilters'] = '';
      return;
    }

    if (
      typeof parsedSegmentationFilters !== 'object' ||
      parsedSegmentationFilters === null
    ) {
      // Clear filters that are valid JSON but not an object, e.g. `null`
      params['segmentationFilters'] = '';
      return;
    }

    // Clear the filters if you've got any on a breakdown other than subscription id
    if (
      (reference === 'budget' || reference === 'forecast') &&
      Object.keys(parsedSegmentationFilters).some(
        (segmentationId) => segmentationId !== 'subscription_id',
      )
    ) {
      params['segmentationFilters'] = '';
    }
  }

  checkSelectedKpi(params: QueryParams & { kpi?: string }): void {
    const { kpi } = params;

    if (kpi && !(VALID_KPIS as readonly string[]).includes(kpi)) {
      params['kpi'] = DEFAULT_QP_VALUES.hotel['revenue-insight'].kpi;
    }
  }

  checkSelectedKpiCategory(params: QueryParams): void {
    const { kpiCategory } = params;

    if (
      kpiCategory !== undefined &&
      !this.access.hasFeature('revenueinsight.transient_group_filter') &&
      kpiCategory !== KPI_CATEGORIES.TOTAL
    ) {
      params['kpiCategory'] = KPI_CATEGORIES.TOTAL;
    }
  }

  checkSelectedKpiCategoryForTargets(params: QueryParams): void {
    const { reference } = params;

    // Can only have kpiCategory total for budget or forecast
    if (
      (reference === 'budget' || reference === 'forecast') &&
      !this.isTopLevelKpiCategorySelected(params)
    ) {
      params['kpiCategory'] = KPI_CATEGORIES.TOTAL;
    }
  }

  checkSelectedMetric(
    params: QueryParams & { selectedMetric?: IssueType },
  ): void {
    const { selectedMetric } = params;

    if (
      selectedMetric &&
      !Object.values(ISSUE_TYPES).includes(selectedMetric)
    ) {
      params['selectedMetric'] = ISSUE_TYPES.MAJOR_OTA;
    }
  }

  checkSelectedView(params: QueryParams): void {
    // Cancellations view is not available on Revenue Insight multi property.
    if (params['selectedView'] === 'cancellations') {
      params['selectedView'] = 'net';
    }
  }

  checkShowMemberRate(params: QueryParams): void {
    if (typeof params['showMemberRate'] !== 'boolean') {
      params['showMemberRate'] = false;
    }
  }

  checkSnapshotsFromToDates(
    validatedQueryParams: Pick<MarketSnapshotsParams, 'fromDate' | 'toDate'>,
  ): void {
    const { fromDate, toDate } = validatedQueryParams;

    if (
      fromDate !== undefined &&
      !dayjs(fromDate, 'YYYY-MM-DD', true).isValid()
    ) {
      validatedQueryParams['fromDate'] = undefined;
    }

    if (toDate !== undefined && !dayjs(toDate, 'YYYY-MM-DD', true).isValid()) {
      validatedQueryParams['toDate'] = undefined;
    }

    const isFromDatePresentWithoutToDate =
      fromDate !== undefined && toDate === undefined;
    const isToDatePresentWithoutFromDate =
      toDate !== undefined && fromDate === undefined;

    if (isFromDatePresentWithoutToDate || isToDatePresentWithoutFromDate) {
      validatedQueryParams['fromDate'] = undefined;
      validatedQueryParams['toDate'] = undefined;
    }

    const isFromDateAfterToDate =
      fromDate !== undefined &&
      toDate !== undefined &&
      dayjs(fromDate).isAfter(toDate);

    if (isFromDateAfterToDate) {
      validatedQueryParams['fromDate'] = undefined;
      validatedQueryParams['toDate'] = undefined;
    }
  }

  checkSnapshotsGroupBy(
    validatedQueryParams: Pick<MarketSnapshotsParams, 'groupBy'>,
  ): void {
    if (
      validatedQueryParams.groupBy !== undefined &&
      !isGroupBy(validatedQueryParams.groupBy)
    ) {
      validatedQueryParams['groupBy'] = undefined;
    }
  }

  // Month view needs whole months; the single snap site for picks, switches and deep links.
  checkSnapshotsMonthRange(
    validatedQueryParams: Pick<
      MarketSnapshotsParams,
      'fromDate' | 'groupBy' | 'toDate'
    >,
  ): void {
    const { fromDate, groupBy, toDate } = validatedQueryParams;

    if (groupBy === GROUP_BY.MONTH && fromDate && toDate) {
      validatedQueryParams.fromDate = dayjs(fromDate)
        .startOf('month')
        .format('YYYY-MM-DD');
      validatedQueryParams.toDate = dayjs(toDate)
        .endOf('month')
        .format('YYYY-MM-DD');
    }
  }

  checkSnapshotsPagination(
    validatedQueryParams: Pick<MarketSnapshotsParams, 'page' | 'pageSize'>,
  ): void {
    const { page, pageSize } = validatedQueryParams;

    if (page !== undefined && (!Number.isInteger(page) || page < 1)) {
      validatedQueryParams['page'] = undefined;
    }

    if (
      pageSize !== undefined &&
      (!Number.isInteger(pageSize) || pageSize < 1)
    ) {
      validatedQueryParams['pageSize'] = undefined;
    }
  }

  checkSortKeysAndOptions(
    params: QueryParams & { sortKey?: ParityFilter; sortType?: SortType },
  ): void {
    const { sortType, sortKey } = params;

    if (sortType && !Object.values(SORT_TYPES).includes(sortType)) {
      params['sortType'] = DEFAULT_QP_VALUES.market.parity.insight.sortType;
    }

    if (sortKey && !Object.values(PARITY_FILTER).includes(sortKey)) {
      params['sortKey'] = DEFAULT_QP_VALUES.market.parity.insight.sortKey;
    }
  }

  /**
   * Table view of DRP hotels: keep a valid range, otherwise fill in the default range.
   * Anywhere else the range is removed.
   */
  checkStayDateRangePricingAssistant(
    params: QueryParams & {
      fromDate?: string | null;
      toDate?: string | null;
      view?: string;
    },
  ): void {
    const { fromDate, toDate, view } = params;

    if (!isStayDateRangeEnabled(this.featureToggle, this.access, view)) {
      if (fromDate != null || toDate != null) {
        params['fromDate'] = undefined;
        params['toDate'] = undefined;
      }
      return;
    }

    const today = this.hotelAndMarketService.getLocalTime();
    // The week picker's earliest week can start last month, and it writes that whole month as the range
    const firstSupportedDate = today
      .subtract(1, 'month')
      .startOf('month')
      .format('YYYY-MM-DD');
    const lastSupportedDate = today
      .add(MAX_ALLOWED_MONTHS_IN_FUTURE, 'month')
      .endOf('month')
      .format('YYYY-MM-DD');

    const isDate = (date: unknown): date is string =>
      typeof date === 'string' && dayjs(date, 'YYYY-MM-DD', true).isValid();
    // YYYY-MM-DD strings compare in date order, with no timezone to get wrong
    const isValidRange =
      isDate(fromDate) &&
      isDate(toDate) &&
      fromDate >= firstSupportedDate &&
      fromDate <= toDate &&
      toDate <= lastSupportedDate;

    if (isValidRange) {
      return;
    }

    Object.assign(
      params,
      getStayDateRangeQueryParams(getDefaultStayDateRange(today)),
    );
  }

  checkToDate(params: QueryParams): void {
    const { toDate } = params;

    if (toDate) {
      const todayMoment = this.hotelAndMarketService.getLocalTime();
      if (todayMoment.isBefore(toDate as string)) {
        params['toDate'] = todayMoment.format('YYYY-MM-DD');
      }
    }
  }

  checkTotalRevenueFromToDates(
    validatedQueryParams: Pick<MarketTotalRevenueParams, 'fromDate' | 'toDate'>,
  ): void {
    const { fromDate, toDate } = validatedQueryParams;

    if (
      fromDate !== undefined &&
      !dayjs(fromDate, 'YYYY-MM-DD', true).isValid()
    ) {
      validatedQueryParams['fromDate'] = undefined;
    }

    if (toDate !== undefined && !dayjs(toDate, 'YYYY-MM-DD', true).isValid()) {
      validatedQueryParams['toDate'] = undefined;
    }

    const isFromDatePresentWithoutToDate =
      fromDate !== undefined && toDate === undefined;
    const isToDatePresentWithoutFromDate =
      toDate !== undefined && fromDate === undefined;

    if (isFromDatePresentWithoutToDate || isToDatePresentWithoutFromDate) {
      validatedQueryParams['fromDate'] = undefined;
      validatedQueryParams['toDate'] = undefined;
    }

    const isFromDateAfterToDate =
      fromDate !== undefined &&
      toDate !== undefined &&
      dayjs(fromDate).isAfter(dayjs(toDate));

    if (isFromDateAfterToDate) {
      validatedQueryParams['fromDate'] = undefined;
      validatedQueryParams['toDate'] = undefined;
    }

    const isToDateAfterMaxDate =
      toDate !== undefined &&
      this.hotelAndMarketService
        .toLocalTime(toDate)
        .isAfter(this.totalRevenueDate.maxDateInLocalTime);

    if (isToDateAfterMaxDate) {
      validatedQueryParams['fromDate'] = undefined;
      validatedQueryParams['toDate'] = undefined;
    }
  }

  checkTotalRevenueLocalReference(
    params: Pick<
      MarketTotalRevenueParams,
      'localReference' | 'referencePeriods'
    >,
  ): void {
    const { localReference, referencePeriods } = params;

    // Check if the localReference is allowed
    const isLocalReferenceAllowed = referencePeriods.includes(localReference);
    if (!isLocalReferenceAllowed && referencePeriods[0]) {
      params['localReference'] = referencePeriods[0];
    }
  }

  checkTotalRevenueReferencePeriods(
    params: Pick<
      MarketTotalRevenueParams,
      'referencePeriods' | 'referencePeriodCustomDate'
    >,
  ): void {
    const defaultReferencePeriods =
      DEFAULT_QP_VALUES.market['total-revenue'].referencePeriods;
    const defaultReferencePeriodCustomDate = undefined;

    const { referencePeriods, referencePeriodCustomDate } = params;

    // Check if the number of periods is allowed
    const isAllowedNumberOfPeriods =
      referencePeriods.length !== 0 &&
      referencePeriods.length <= MAX_NUMBER_OF_REFERENCE_PERIODS;
    if (!isAllowedNumberOfPeriods) {
      params['referencePeriods'] = defaultReferencePeriods;
      params['referencePeriodCustomDate'] = defaultReferencePeriodCustomDate;
      return;
    }

    // Check if the periods are allowed
    const isAllowedPeriods = referencePeriods.every((period) =>
      ALLOWED_REFERENCE_PERIODS.includes(period),
    );
    if (!isAllowedPeriods) {
      params['referencePeriods'] = defaultReferencePeriods;
      params['referencePeriodCustomDate'] = defaultReferencePeriodCustomDate;
      return;
    }

    // Check if custom period is selected without a custom date
    const isCustomSelected = referencePeriods.includes('custom');
    if (isCustomSelected && referencePeriodCustomDate === undefined) {
      params['referencePeriods'] = defaultReferencePeriods;
    }

    // Check for a referencePeriodCustomDate when custom is not selected
    if (referencePeriodCustomDate !== undefined && !isCustomSelected) {
      params['referencePeriodCustomDate'] = defaultReferencePeriodCustomDate;
      return;
    }

    // Check for duplicate periods
    const uniqueReferencePeriods = [...new Set(referencePeriods)];
    if (uniqueReferencePeriods.length !== referencePeriods.length) {
      params['referencePeriods'] = defaultReferencePeriods;
      params['referencePeriodCustomDate'] = defaultReferencePeriodCustomDate;
    }
  }

  checkTotalRevenueTrendAggregation(
    params: Pick<MarketTotalRevenueParams, 'trendAggregation'>,
  ): void {
    const { trendAggregation } = params;

    const DATE_AGGREGATION_OPTIONS = [
      DATE_AGGREGATION.DAY,
      DATE_AGGREGATION.WEEK,
      DATE_AGGREGATION.MONTH,
    ];

    const isTrendAggregationValid =
      DATE_AGGREGATION_OPTIONS.includes(trendAggregation);

    if (!isTrendAggregationValid) {
      params['trendAggregation'] =
        DEFAULT_QP_VALUES.market['total-revenue'].trendAggregation;
    }
  }

  checkTotalRevenueTrendMetric(
    params: Pick<MarketTotalRevenueParams, 'trendMetric'>,
  ): void {
    const { trendMetric } = params;

    const isTrendMetricValid = Object.values(TOTAL_REVENUE_METRICS).includes(
      trendMetric,
    );

    if (!isTrendMetricValid) {
      params['trendMetric'] =
        DEFAULT_QP_VALUES.market['total-revenue'].trendMetric;
    }
  }

  checkTotalRevenueView(
    validatedQueryParams: Pick<MarketTotalRevenueParams, 'view'>,
  ): void {
    const { view } = validatedQueryParams;

    if (
      !this.featureToggle.hasFeatureToggle(
        FEATURE_TOGGLES.TOTAL_REVENUE_STRATEGY_TAB,
      ) &&
      view === TOTAL_REVENUE_VIEWS.STRATEGY
    ) {
      validatedQueryParams['view'] =
        DEFAULT_QP_VALUES.market['total-revenue'].view;
    }

    if (
      ![TOTAL_REVENUE_VIEWS.DEPARTMENTS, TOTAL_REVENUE_VIEWS.STRATEGY].includes(
        view,
      )
    ) {
      validatedQueryParams['view'] =
        DEFAULT_QP_VALUES.market['total-revenue'].view;
    }
  }

  checkTrendsExtractDate(
    params: QueryParams & { trendsExtractDate?: string },
  ): void {
    const trendsExtractDate = dayjs(params['trendsExtractDate'], 'YYYY-MM-DD');
    const today = getTodayUtc();

    if (!trendsExtractDate.isValid()) {
      // Check for dayjs validity
      // Check for dayjs validity
      params['trendsExtractDate'] = today;
    } else if (
      !trendsExtractDate.isSame(trendsExtractDate.startOf('isoWeek'), 'day')
    ) {
      // Check if trendsExtractDate is a Monday!
      // Check if trendsExtractDate is a Monday!
      params['trendsExtractDate'] = trendsExtractDate
        .startOf('isoWeek')
        .format('YYYY-MM-DD');
    } else if (trendsExtractDate.isAfter(today)) {
      // Checks and resets any future date to the last possible trends extract date
      // Checks and resets any future date to the last possible trends extract date
      params['trendsExtractDate'] = today;
    } else if (
      trendsExtractDate.isBefore(
        this.hotelAndMarketService.hotelOrMarket.parityFirstExtractDate,
      )
    ) {
      // Checks and resets any past date to the last possible trends extract date
      // Checks and resets any past date to the last possible trends extract date
      params['trendsExtractDate'] = today;
    }
  }

  checkView(validatedQueryParams: QueryParams & { view?: string }): void {
    // Extra check because we had users ending up on a blank page because of an incorrect view parameter
    if (
      validatedQueryParams['view'] &&
      ['calendar', 'chart', 'table'].indexOf(validatedQueryParams['view']) ===
        -1
    ) {
      validatedQueryParams['view'] = 'calendar';
    }
  }

  checkWeekDefaultPricingAssistant(
    params: QueryParams & { month?: string; view?: string; week?: string },
  ): void {
    const { week, month, view } = params;
    const now = this.hotelAndMarketService.getLocalTime();

    // For calendar view, week should be unset.
    if (view === PRIMA_VIEWS.CALENDAR) {
      params['week'] = undefined;
      return;
    }

    // For table view:
    if (view === PRIMA_VIEWS.TABLE) {
      // If a week is provided, always normalize it to the start of its week.
      if (week) {
        params['week'] = this.hotelAndMarketService
          .toLocalTime(week)
          .startOf('week')
          .format('YYYY-MM-DD');
        return;
      }

      // If no week is set, but month is available...
      if (!week && month) {
        // Parse the provided month.
        const monthTime = this.hotelAndMarketService.toLocalTime(month);
        if (monthTime.isSame(now, 'month')) {
          // If the provided month is the current month, set week to the start of the current week.
          params['week'] = now.startOf('week').format('YYYY-MM-DD');
        } else {
          // If the provided month is not the current month, set week to the start of that month.
          params['week'] = monthTime
            .startOf('month')
            .startOf('week')
            .format('YYYY-MM-DD');
        }
      }
    }
  }

  // With an active stay date range, `week` follows the range instead of `month`
  checkWeekPricingAssistant(
    params: QueryParams & {
      fromDate?: string;
      month?: string;
      toDate?: string;
      view?: string;
      week?: string;
    },
  ): void {
    const { fromDate, toDate, view, week } = params;

    if (
      !fromDate ||
      !toDate ||
      !isStayDateRangeEnabled(this.featureToggle, this.access, view)
    ) {
      this.checkWeekDefaultPricingAssistant(params);
      this.checkWeekWithinAllowedPricingAssistantRange(params);
      return;
    }

    const rangeStart = this.hotelAndMarketService.toLocalTime(fromDate);
    const rangeEnd = this.hotelAndMarketService.toLocalTime(toDate);
    const weekStart = week
      ? this.hotelAndMarketService.toLocalTime(week).startOf('week')
      : null;

    const weekOverlapsRange =
      weekStart !== null &&
      weekStart.isValid() &&
      !weekStart.isAfter(rangeEnd, 'day') &&
      !weekStart.endOf('week').isBefore(rangeStart, 'day');

    // Keep a week that overlaps the range, otherwise show the range's own week
    params['week'] = weekOverlapsRange
      ? weekStart.format('YYYY-MM-DD')
      : getWeekForStayDateRange(
          rangeStart,
          this.hotelAndMarketService.getLocalTime(),
        );
  }

  checkWeekWithinAllowedPricingAssistantRange(
    params: QueryParams & { month?: string; view?: string; week?: string },
  ): void {
    const { week, month, view } = params;
    const now = this.hotelAndMarketService.getLocalTime();

    // If the view is calendar, there is no week to check.
    if (view === PRIMA_VIEWS.CALENDAR) {
      return;
    }

    // If both month and week are provided, ensure the week falls within the month.
    const monthStart = this.hotelAndMarketService
      .toLocalTime(month)
      .startOf('month');

    const weekStart = this.hotelAndMarketService
      .toLocalTime(week)
      .startOf('week');

    if (!weekStart.isSame(monthStart, 'month')) {
      // Edge case: In table view, we can select a week that is part in current month and part in previous month
      // If the month start is more than one month behind the current time, adjust using the month end.
      if (monthStart.isBefore(now.subtract(1, 'month'))) {
        params['week'] = this.hotelAndMarketService
          .toLocalTime(month)
          .endOf('month')
          .startOf('week')
          .format('YYYY-MM-DD');
        // Otherwise, reset to the week starting at the beginning of the provided month.
      } else if (monthStart.isSame(now, 'month')) {
        // If the provided month is the current month, set week to the start of the current week.
        params['week'] = now.startOf('week').format('YYYY-MM-DD');
      } else {
        // If the provided month is not the current month, set week to the start of that month.
        params['week'] = monthStart
          .startOf('month')
          .startOf('week')
          .format('YYYY-MM-DD');
      }
    }
  }

  getDay(params: QueryParams & { day?: string }): string {
    let day =
      params['day'] ??
      this.hotelAndMarketService.getLocalTime().format('YYYY-MM');

    // YYYY-MM formats should be converted to YYYY-MM-01
    if (/^[0-9]{4}-[0-9]{2}$/.test(day)) {
      day = `${day}-01`;
    }

    return day;
  }

  getMonth(params: QueryParams & { month?: string }): string {
    let month =
      params['month'] ??
      this.hotelAndMarketService.getLocalTime().format('YYYY-MM');

    // YYYY-MM formats should be converted to YYYY-MM-01
    if (/^[0-9]{4}-[0-9]{2}$/.test(month)) {
      month = `${month}-01`;
    }

    return month;
  }

  getOtaObj(otaId: string): Ota | null {
    return this.store.peekRecord<Ota>('ota', otaId);
  }

  // If there is difference between the two objects, one of the the QPs has been updated due to being invalid
  getQueryParamObjects(transition: Transition): {
    originalQueryParams: QueryParams;
    validatedQueryParams: QueryParams;
  } {
    const { queryParams } = transition.to;
    const validatedQueryParams = { ...queryParams };

    return {
      originalQueryParams: queryParams,
      validatedQueryParams,
    };
  }

  isTopLevelKpiCategorySelected(params: QueryParams): boolean {
    const { kpiCategory } = params;
    return kpiCategory === undefined || kpiCategory === KPI_CATEGORIES.TOTAL;
  }

  redirectToEnabledTab(transition?: Transition): void {
    // In RI upsell a redirectToEnabledTab is manually triggered (= no ongoing transition)
    if (transition) {
      transition.abort();
    }

    const featureFlags = Array.from(this.access.featureFlags);

    if (this.hotelAndMarketService.hotel) {
      if (this.access.hasOverviewDashboardTab) {
        this.router.transitionTo('hotel.overview', this.hotelOrMarket);
        return;
      }
      if (this.access.hasSmartDistributionTab) {
        this.router.transitionTo('hotel.channel-pricing', this.hotelOrMarket);
        return;
      }
      if (this.access.hasPricingAssistantTab) {
        this.router.transitionTo('hotel.pricing-manager', this.hotelOrMarket);
        return;
      }
      if (this.access.hasFeature('app.market_insight')) {
        this.router.transitionTo('hotel.market-insight', this.hotelOrMarket);
        return;
      }
      if (this.access.hasRates) {
        this.router.transitionTo('hotel.rates', this.hotelOrMarket);
        return;
      }
      if (this.access.hasReviewManagerTab) {
        this.router.transitionTo('hotel.reviews', this.hotelOrMarket);
        return;
      }
      if (this.access.hasEvents) {
        this.router.transitionTo('hotel.events', this.hotelOrMarket);
        return;
      }
      if (this.access.hasParityOverview) {
        this.router.transitionTo('hotel.parity.overview', this.hotelOrMarket);
        return;
      }
      if (this.access.hasParity) {
        if (this.access.hasParityAnalytics) {
          this.router.transitionTo('hotel.parity.insight', this.hotelOrMarket);
          return;
        }
        this.router.transitionTo('hotel.parity.list', this.hotelOrMarket);
      }

      if (this.access.hasParityAnalyticsStaff) {
        this.router.transitionTo('hotel.parity.insight', this.hotelOrMarket, {
          queryParams: { hasParityAnalytics: true },
        });
        return;
      }

      if (this.access.hasRanking) {
        this.router.transitionTo('hotel.ranking', this.hotelOrMarket);
        return;
      }
      if (this.access.hasFeature('app.revenue_insight')) {
        this.router.transitionTo('hotel.revenue-insight', this.hotelOrMarket);
        return;
      }

      if (this.access.hasBenchmarkInsight) {
        this.router.transitionTo('hotel.benchmark-insight', this.hotelOrMarket);
        return;
      }

      if (
        this.access.hasFeature('app.net_revenue.view') &&
        this.featureToggle.hasFeatureToggle(FEATURE_TOGGLES.BI_NET_REVENUE)
      ) {
        this.router.transitionTo('hotel.net-revenue', this.hotelOrMarket);
        return;
      }

      if (this.access.hasSpider) {
        this.router.transitionTo('hotel.snapshot', this.hotelOrMarket);
        return;
      }

      if (this.access.hasChannelManagerTab) {
        this.router.transitionTo('hotel.channel-manager', this.hotelOrMarket);
        return;
      }
      if (this.access.hasReservationManagerTab) {
        this.router.transitionTo(
          'hotel.reservation-manager',
          this.hotelOrMarket,
        );
        return;
      }

      this.errorTracking.captureMessage('No tabs for this hotel!', {
        featureFlags,
      });

      this.router.transitionTo('hotel.error', this.hotelOrMarket, {
        queryParams: { reason: 'error.no_tabs' },
      });
    } else if (this.hotelAndMarketService.market) {
      if (this.access.hasSmartInsights) {
        this.router.transitionTo('market.smart-insights', this.hotelOrMarket);
        return;
      }
      if (this.access.hasFeature('app.market.rates')) {
        this.router.transitionTo('market.marketrates', this.hotelOrMarket);
        return;
      }
      if (this.access.hasParityInsightAccess) {
        this.router.transitionTo('market.parity.insight', this.hotelOrMarket);
        return;
      }
      if (this.access.hasFeature('app.market.revenue_insight')) {
        this.router.transitionTo('market.revenue-insight', this.hotelOrMarket);
        return;
      }

      if (this.access.hasSpiderMarket) {
        this.router.transitionTo('market.executive', this.hotelOrMarket);
        return;
      }

      if (
        this.access.hasFeature('app.market.reservations') &&
        this.featureToggle.hasFeatureToggle(FEATURE_TOGGLES.BI_SNAPSHOTS)
      ) {
        this.router.transitionTo('market.snapshots', this.hotelOrMarket);
        return;
      }

      if (this.access.hasTotalRevenue) {
        this.router.transitionTo('market.total-revenue', this.hotelOrMarket);
      }

      if (this.access.hasCustomReporting) {
        this.router.transitionTo('market.custom-reporting', this.hotelOrMarket);
        return;
      }

      // ToDo: Add location?
      // this.errorTracking.captureException(`No tabs for this market! Via ${(transition as TransitionWithTarget).targetName}`);
      this.errorTracking.captureMessage('No tabs for this market!', {
        featureFlags,
      });

      this.router.transitionTo('market.error', this.hotelOrMarket, {
        queryParams: { reason: 'error.no_tabs' },
      });
    } else if (this.destination.destinationSubscription) {
      if (this.access.hasFeature('app.daas.custom_reporting')) {
        this.router.transitionTo(
          'destination.reporting',
          this.destination.destinationSubscription.id,
        );
      }

      this.errorTracking.captureMessage('No tabs for this destination!', {
        featureFlags,
      });

      this.router.transitionTo(
        'destination.error',
        this.destination.destinationSubscription.id,
        {
          queryParams: { reason: 'error.no_tabs' },
        },
      );
    }
  }

  // Internal helpers
  redirectToTab(transition: Transition, redirectRoute: string): void {
    transition.abort();
    this.router.transitionTo(redirectRoute, this.hotelOrMarket);
  }

  validateBenchmarkInsightCompetitorsQueryParams(transition: Transition): void {
    const { originalQueryParams, validatedQueryParams } =
      this.getQueryParamObjects(transition);

    this.checkBenchmarkInsightCompetitorsActiveTab(validatedQueryParams);

    this.validateQueryParams(
      transition,
      originalQueryParams,
      validatedQueryParams,
      'hotel.settings.competitors.benchmark',
    );
  }

  validateBenchmarkInsightMarketCompositionQueryParams(
    transition: Transition,
  ): void {
    const { originalQueryParams, validatedQueryParams } =
      this.getQueryParamObjects(transition);

    this.checkBenchmarkInsightMarketCompositionActiveTab(validatedQueryParams);

    this.validateQueryParams(
      transition,
      originalQueryParams,
      validatedQueryParams,
      (transition as TransitionWithTarget).targetName,
    );
  }

  validateBenchmarkInsightQueryParams(transition: Transition): void {
    const { originalQueryParams, validatedQueryParams } =
      this.getQueryParamObjects(transition);

    this.checkBenchmarkInsightSelectedKpiHeroChart(validatedQueryParams);
    this.checkBenchmarkInsightSelectedKpiLosChart(validatedQueryParams);
    this.checkBenchmarkInsightSelectedLosSortOption(validatedQueryParams);
    this.checkBenchmarkInsightSelectedLosOccupancyView(validatedQueryParams);
    this.checkBenchmarkInsightSelectedAxisOption(validatedQueryParams);
    this.checkBenchmarkInsightStayDateSectionView(validatedQueryParams);
    this.checkBenchmarkInsightDateAggregation(validatedQueryParams);
    this.checkBenchmarkInsightDateRange(validatedQueryParams);
    this.checkBenchmarkInsightPickup(validatedQueryParams);
    this.checkBenchmarkInsightReferencePeriod(validatedQueryParams);
    // checkBenchmarkInsightDataSets is deferred to the benchmark-insight route's model() hook,
    // after the BENI hotels endpoint has resolved and allowedComparisonSets is available.
    this.checkBenchmarkInsightHiddenFutureCompsetData(validatedQueryParams);

    this.validateQueryParams(
      transition,
      originalQueryParams,
      validatedQueryParams,
      (transition as TransitionWithTarget).targetName,
    );
  }

  validateBenchmarkInsightSmartCompsetBuilderQueryParams(
    transition: Transition,
  ): void {
    const { originalQueryParams, validatedQueryParams } =
      this.getQueryParamObjects(transition);
    const { hasCompsetBenchmarkSmartCompset } = this.access;

    if (hasCompsetBenchmarkSmartCompset) {
      validatedQueryParams['activeTab'] = BENI_COMPETITORS_TABS.SMART_COMPSET;
    }

    this.validateQueryParams(
      transition,
      originalQueryParams,
      validatedQueryParams,
      'hotel.settings.competitors.benchmark.smart-compset',
    );
  }

  validateBenchmarkQueryParams(transition: Transition): void {
    const { originalQueryParams, validatedQueryParams } =
      this.getQueryParamObjects(transition);

    this.checkMonthDefault(validatedQueryParams);

    this.validateQueryParams(
      transition,
      originalQueryParams,
      validatedQueryParams,
      'hotel.benchmark',
    );
  }

  validateBudgetAndForecastEntryIdQueryParam(
    transition: Transition,
    entries: BudgetForecastEntry[],
  ): boolean {
    const { originalQueryParams, validatedQueryParams } =
      this.getQueryParamObjects(transition);

    const entryIdQP = originalQueryParams['entryId'];
    const selectedEntry = entryIdQP
      ? entries.find((entry) => entry.id === entryIdQP)
      : undefined;

    if (selectedEntry) {
      return true;
    }

    const mainEntry = entries.find((entry) => entry.isMain)!;
    validatedQueryParams['entryId'] = mainEntry.id;

    this.validateQueryParams(
      transition,
      originalQueryParams,
      validatedQueryParams,
      (transition as TransitionWithTarget).targetName,
    );

    return false;
  }

  validateBudgetForecastEntryIdRouteParam(transition: Transition): void {
    const entryId = transition.to.params?.['entry_id'];

    const isNumber = !Number.isNaN(Number.parseInt(entryId as string));
    if (!isNumber) {
      const route =
        transition.targetName === 'hotel.settings.budget.detail'
          ? 'hotel.settings.budget'
          : 'hotel.settings.forecast';
      this.router.transitionTo(route);
    }
  }

  validateCentralMappingMenuQueryParams(
    transition: Transition,
    originalQueryParams: HotelSettingsCentralMappingMenuParams,
  ): void {
    const validatedQueryParams: QueryParams = {
      ...originalQueryParams,
    };

    this.checkCentralMappingMenuView(validatedQueryParams);

    this.validateQueryParams(
      transition,
      originalQueryParams,
      validatedQueryParams,
      transition.to.name,
    );
  }

  validateCentralMappingQueryParams(
    transition: Transition,
    originalQueryParams: QueryParams,
  ): void {
    const validatedQueryParams: QueryParams = {
      ...originalQueryParams,
    };

    this.checkCentralMappingUserIsStaffUser(validatedQueryParams);
    this.checkCentralMappingBreakdown(validatedQueryParams);

    if (transition.to?.localName === 'mapping') {
      this.checkCentralMappingValueType(validatedQueryParams);
      this.checkCentralMappingPagination(validatedQueryParams);
    }

    this.validateQueryParams(
      transition,
      originalQueryParams,
      validatedQueryParams,
      transition.to.name,
    );
  }

  validateChannelPricingQueryParams(transition: Transition): void {
    const { originalQueryParams, validatedQueryParams } =
      this.getQueryParamObjects(transition);

    const startOfCurrentWeekDayjs = this.hotelAndMarketService
      .getLocalTime()
      .startOf('week')
      .startOf('day');

    if (!validatedQueryParams['fromDate']) {
      validatedQueryParams['fromDate'] =
        startOfCurrentWeekDayjs.format('YYYY-MM-DD');
    }

    if (
      validatedQueryParams['fromDate'] &&
      this.hotelAndMarketService
        .toLocalTime(validatedQueryParams['fromDate'] as string)
        .startOf('day')
        .isBefore(startOfCurrentWeekDayjs, 'day')
    ) {
      validatedQueryParams['fromDate'] =
        startOfCurrentWeekDayjs.format('YYYY-MM-DD');
    }

    const maxAllowedDate = this.hotelAndMarketService
      .getLocalTime()
      .add(12, 'month')
      .endOf('week');

    if (
      maxAllowedDate.isBefore(validatedQueryParams['fromDate'] as string, 'day')
    ) {
      validatedQueryParams['fromDate'] =
        startOfCurrentWeekDayjs.format('YYYY-MM-DD');
    }

    this.validateQueryParams(
      transition,
      originalQueryParams,
      validatedQueryParams,
      (transition as TransitionWithTarget).targetName,
    );
  }

  validateEventsQueryParams(
    transition: Transition,
    originalQueryParams: QueryParams,
  ): void {
    const validatedQueryParams = { ...originalQueryParams };

    this.checkMonthDefault(validatedQueryParams);
    this.validateQueryParams(
      transition,
      originalQueryParams,
      validatedQueryParams,
      'hotel.events',
    );
  }

  validateHotelSettingsQueryParams(transition: Transition): void {
    const { originalQueryParams, validatedQueryParams } =
      this.getQueryParamObjects(transition);
    const transitionFrom = transition.from?.name;

    this.checkHotelId(validatedQueryParams, transitionFrom);

    this.validateQueryParams(
      transition,
      originalQueryParams,
      validatedQueryParams,
      'hotel.settings.hotels',
    );
  }

  // Single Property
  validateMarketInsightQueryParams(transition: Transition): void {
    const { originalQueryParams, validatedQueryParams } =
      this.getQueryParamObjects(transition);

    this.checkMIDates(validatedQueryParams);
    this.checkMarketInsightFullParameters(validatedQueryParams);

    this.validateQueryParams(
      transition,
      originalQueryParams,
      validatedQueryParams,
      'hotel.market-insight',
    );
  }

  // Multi Property
  validateMarketRatesQueryParams(transition: Transition): void {
    const { originalQueryParams, validatedQueryParams } =
      this.getQueryParamObjects(transition);

    this.checkMaxPersonsDefault(validatedQueryParams);
    this.checkMaxPersonsForMarketRates(validatedQueryParams);

    // ToDo: Probably best to perform this check first?
    const { otaObj, reason } =
      this.checkOtaSelectionForMarketRates(validatedQueryParams);
    if (reason) {
      transition.abort();
      this.router.transitionTo('market.error', this.hotelOrMarket, {
        queryParams: { reason },
      });
      return;
    }
    this.checkDayWithinRange(validatedQueryParams);
    this.checkLos(
      validatedQueryParams,
      otaObj,
      this.hotelOrMarket.defaultLosRates,
    );
    this.checkRateParamForMarketRates(validatedQueryParams, otaObj);
    this.checkMaxPersonsForRates(validatedQueryParams, otaObj);
    this.checkMealplan(validatedQueryParams, otaObj);
    this.checkRoomType(validatedQueryParams, otaObj);
    this.checkBestFlex(validatedQueryParams, otaObj);
    this.checkMembershipType(validatedQueryParams, otaObj);
    this.checkPlatform(validatedQueryParams, otaObj);
    this.checkCompare(
      validatedQueryParams,
      otaObj,
      this.getDay(originalQueryParams),
    );
    this.checkBrands(validatedQueryParams);
    this.checkManagementTypes(validatedQueryParams);
    this.checkExpandedRowIds(validatedQueryParams);

    this.validateQueryParams(
      transition,
      originalQueryParams,
      validatedQueryParams,
      'market.marketrates',
    );
  }

  validateMarketSnapshotsQueryParams(
    transition: Transition,
    originalQueryParams: MarketSnapshotsParams,
    routeName: string,
  ): void {
    const validatedQueryParams = { ...originalQueryParams };

    this.checkSnapshotsGroupBy(validatedQueryParams);
    this.checkSnapshotsFromToDates(validatedQueryParams);
    this.checkSnapshotsPagination(validatedQueryParams);

    this.checkSnapshotsMonthRange(validatedQueryParams);
    this.validateQueryParams(
      transition,
      originalQueryParams,
      validatedQueryParams,
      routeName,
    );
  }

  validateNetRevenueQueryParams(
    transition: Transition,
    params: NetRevenueQueryParams,
  ): void {
    const validatedQueryParams = { ...params };

    this.checkNetRevenueComparisonMode(validatedQueryParams);
    this.checkNetRevenueDates(validatedQueryParams);
    this.checkNetRevenueComparisonFromDate(validatedQueryParams);

    this.validateQueryParams(
      transition,
      params,
      validatedQueryParams,
      'hotel.net-revenue',
    );
  }

  validateOverviewDashboardQueryParams(transition: Transition): void {
    const { originalQueryParams, validatedQueryParams } =
      this.getQueryParamObjects(transition);

    const { reason } = this.checkOtaSelectionForRates(validatedQueryParams);
    if (reason) {
      transition.abort();
      this.router.transitionTo('hotel.error', this.hotelOrMarket, {
        queryParams: { reason },
      });
      return;
    }

    this.checkOverviewDashboardDates(validatedQueryParams);
    this.checkCompsetId(validatedQueryParams);
    this.checkCompset(validatedQueryParams);

    this.validateQueryParams(
      transition,
      originalQueryParams,
      validatedQueryParams,
      'hotel.overview',
    );
  }

  validateParityInsightQueryParamsAfter(
    params: QueryParams,
    config: ParityConfig,
  ): void {
    this.checkLosWithConfig(params, config);
    this.checkPosWithConfig(params, config);

    this.router.replaceWith({ queryParams: params });
  }

  validateParityInsightQueryParamsBefore(params: QueryParams): void {
    const { parityFirstExtractDate: firstExtractDate } =
      this.hotelAndMarketService.hotelOrMarket;

    this.checkMealPlanForParity(params);
    this.checkRateType(params);
    this.checkFromDateType(params);
    this.checkShowMemberRate(params);
    this.checkDefaultDatesForParityInsight(params);
    this.checkExtractDates(params, firstExtractDate);
    this.checkSortKeysAndOptions(params);

    this.router.replaceWith({ queryParams: params });
  }

  validateParityInsightTrendsQueryParams(transition: Transition): void {
    const { originalQueryParams, validatedQueryParams } =
      this.getQueryParamObjects(transition);

    this.checkTrendsExtractDate(validatedQueryParams);

    this.validateQueryParams(
      transition,
      originalQueryParams,
      validatedQueryParams,
      (transition as TransitionWithTarget).targetName,
    );
  }

  validateParityListQueryParams(params: QueryParams): void {
    this.checkMaxPersonsDefault(params);
    this.checkMaxPersonsForParity(params);
    this.checkOtaGroup(params);
    this.checkRateParamForParity(params);
    this.checkMonthDefault(params);
    this.checkMonthInPast(params);
    this.checkParityAnalyticsView(params);
    this.checkPlatformForParity(params);
    this.checkCustomExtractDateForParity(params);

    this.router.replaceWith({ queryParams: params });
  }

  validateParityManagerQueryParams(
    transition: Transition,
    originalQueryParams: QueryParams,
  ): void {
    const validatedQueryParams = { ...originalQueryParams };

    this.checkToDate(validatedQueryParams);
    this.checkSelectedMetric(validatedQueryParams);

    this.validateQueryParams(
      transition,
      originalQueryParams,
      validatedQueryParams,
      'market.parity.parity-manager',
    );
  }

  validateParityOverviewQueryParams(transition: Transition): void {
    const { originalQueryParams, validatedQueryParams } =
      this.getQueryParamObjects(transition);

    this.checkMaxPersonsDefault(validatedQueryParams);

    this.validateQueryParams(
      transition,
      originalQueryParams,
      validatedQueryParams,
      'hotel.parity.overview',
    );
  }

  validateParityStrategyQueryParams(
    transition: Transition,
    originalQueryParams: QueryParams,
  ): void {
    const validatedQueryParams = { ...originalQueryParams };

    this.checkDefaultDatesForParityInsight(validatedQueryParams);
    this.checkMarketSwitchForParityStrategy(validatedQueryParams);
    this.checkExtractDates(validatedQueryParams, PARITY_STRATEGY_DATA_START);
    this.checkIfOnlyOneHotelSelected(validatedQueryParams);

    this.validateQueryParams(
      transition,
      originalQueryParams,
      validatedQueryParams,
      (transition as TransitionWithTarget).targetName,
    );
  }

  validatePmsMappingQueryParams(transition: Transition): void {
    const { originalQueryParams, validatedQueryParams } =
      this.getQueryParamObjects(transition);

    this.checkPmsMappingActiveTab(validatedQueryParams);

    this.validateQueryParams(
      transition,
      originalQueryParams,
      validatedQueryParams,
      transition.to.name,
    );
  }

  validatePOSQueryParams(transition: Transition): void {
    const { originalQueryParams, validatedQueryParams } =
      this.getQueryParamObjects(transition);

    this.checkMaxPersonsDefault(validatedQueryParams);

    this.validateQueryParams(
      transition,
      originalQueryParams,
      validatedQueryParams,
      'hotel.parity.pos',
    );
  }

  validatePricingAssistantQueryParams(transition: Transition): void {
    const { originalQueryParams, validatedQueryParams } =
      this.getQueryParamObjects(transition);

    this.checkPricingAssistantViewQueryParam(validatedQueryParams);
    this.checkPricingAssistantSelectionsQueryParams(validatedQueryParams);
    this.checkStayDateRangePricingAssistant(validatedQueryParams);
    this.checkMonthDefault(validatedQueryParams);
    this.checkMonthInPastPricingAssistant(validatedQueryParams);
    this.checkMonthWithinMaxAllowedPricingAssistantRange(validatedQueryParams);
    this.checkWeekPricingAssistant(validatedQueryParams);
    this.validateQueryParams(
      transition,
      originalQueryParams,
      validatedQueryParams,
      (transition as TransitionWithTarget).targetName,
    );
  }

  // Compare the original QPs with the validated QPs and abort the transition if necessary
  validateQueryParams(
    transition: Transition,
    originalQueryParams: object,
    validatedQueryParams: object,
    redirectRoute: string,
  ): void {
    const originalKeys = Object.keys(originalQueryParams);
    const validatedKeys = Object.keys(validatedQueryParams);
    // Abort the current transition if the original query param object and the validated query param object are not the same
    if (
      originalKeys.length !== validatedKeys.length ||
      originalKeys.some(
        (key) =>
          originalQueryParams[key as keyof typeof originalQueryParams] !==
          validatedQueryParams[key as keyof typeof validatedQueryParams],
      )
    ) {
      transition.abort();
      this.router.transitionTo(redirectRoute, this.hotelOrMarket.id, {
        queryParams: validatedQueryParams,
      });
    }
  }

  validateRankingQueryParams(transition: Transition): void {
    const { originalQueryParams, validatedQueryParams } =
      this.getQueryParamObjects(transition);

    if (this.hotelOrMarket?.OTAsRanking?.length === 0) {
      transition.abort();
      this.router.transitionTo('hotel.error', this.hotelOrMarket, {
        queryParams: { reason: 'error.no_ranking_otas_configured' },
      });
      return;
    }

    this.checkOtasForRanking(validatedQueryParams);
    this.checkCompsetId(validatedQueryParams);
    this.checkCompset(validatedQueryParams);

    this.validateQueryParams(
      transition,
      originalQueryParams,
      validatedQueryParams,
      'hotel.ranking',
    );
  }

  validateRatesQueryParams(transition: Transition): void {
    const { originalQueryParams, validatedQueryParams } =
      this.getQueryParamObjects(transition);

    const { otaObj, reason } =
      this.checkOtaSelectionForRates(validatedQueryParams);
    if (reason) {
      transition.abort();
      this.router.transitionTo('hotel.error', this.hotelOrMarket, {
        queryParams: { reason },
      });
      return;
    }

    this.checkMaxPersonsDefault(validatedQueryParams);
    this.checkMonthDefault(validatedQueryParams);
    this.checkMonthWithinRange(validatedQueryParams);
    this.checkView(validatedQueryParams);

    this.checkLos(
      validatedQueryParams,
      otaObj,
      this.hotelOrMarket.defaultLosRates,
    );
    this.checkRateParamForRates(validatedQueryParams, otaObj);
    this.checkMaxPersonsForRates(validatedQueryParams, otaObj);
    this.checkMealplan(validatedQueryParams, otaObj);
    this.checkRoomType(validatedQueryParams, otaObj);
    this.checkBestFlex(validatedQueryParams, otaObj);
    this.checkMembershipType(validatedQueryParams, otaObj);
    this.checkPlatform(validatedQueryParams, otaObj);
    this.checkCompare(
      validatedQueryParams,
      otaObj,
      this.getMonth(originalQueryParams),
    );
    this.checkCompsetId(validatedQueryParams);
    this.checkCompset(validatedQueryParams);
    this.checkCustomExtractDate(validatedQueryParams);

    this.validateQueryParams(
      transition,
      originalQueryParams,
      validatedQueryParams,
      (transition as TransitionWithTarget).targetName,
    );
  }

  validateRateStrategyQueryParams(
    transition: Transition,
    originalQueryParams: QueryParams,
  ): void {
    const validatedQueryParams = { ...originalQueryParams };

    this.checkCompetitorFilter(validatedQueryParams);

    this.validateQueryParams(
      transition,
      originalQueryParams,
      validatedQueryParams,
      'hotel.strategy',
    );
  }

  validateRevenueInsightMultiPropertyQueryParams(transition: Transition): void {
    const { originalQueryParams, validatedQueryParams } =
      this.getQueryParamObjects(transition);

    this.checkSegmentationFilters(validatedQueryParams);
    this.checkFilterLimits(validatedQueryParams);
    this.checkSelectedKpi(validatedQueryParams);
    this.checkRoomsTab(validatedQueryParams);
    this.checkKpiAndRoomsTabMatch(validatedQueryParams);
    this.checkRevenueTab(validatedQueryParams);
    this.checkKpiAndRevenueTabMatch(validatedQueryParams);
    this.checkSelectedKpiCategoryForTargets(validatedQueryParams);
    this.checkHidePickupLY(validatedQueryParams);
    this.checkSelectedView(validatedQueryParams);

    this.validateQueryParams(
      transition,
      originalQueryParams,
      validatedQueryParams,
      'market.revenue-insight',
    );
  }

  validateRevenueInsightQueryParams(transition: Transition): void {
    const { originalQueryParams, validatedQueryParams } =
      this.getQueryParamObjects(transition);

    this.checkProfile(validatedQueryParams);
    this.checkDisableDataContinuity(validatedQueryParams);
    this.checkDemoFreezeDate(validatedQueryParams);
    this.checkSelectedKpi(validatedQueryParams);
    this.checkSelectedKpiCategory(validatedQueryParams);
    this.checkRoomsTab(validatedQueryParams);
    this.checkKpiAndRoomsTabMatch(validatedQueryParams);
    this.checkRevenueTab(validatedQueryParams);
    this.checkKpiAndRevenueTabMatch(validatedQueryParams);
    this.checkHidePickupLY(validatedQueryParams);
    this.checkLeadtime(validatedQueryParams);
    this.checkReference(validatedQueryParams);
    this.checkRIDates(validatedQueryParams);
    this.checkSegmentationFilters(validatedQueryParams);
    this.checkSelectedKpiCategoryForTargets(validatedQueryParams);
    this.checkCustomExtractDate(validatedQueryParams);
    this.checkRevenueInsightSelectedView(validatedQueryParams);

    this.hasVisitedRevenueInsight = true;

    this.validateQueryParams(
      transition,
      originalQueryParams,
      validatedQueryParams,
      'hotel.revenue-insight',
    );
  }

  validateShortTermRentalQueryParams(
    transition: Transition,
    originalQueryParams: QueryParams,
  ): void {
    const validatedQueryParams: QueryParams & {
      dateAggregation?: string;
      fromDate?: string;
      radius?: number;
      refDaysAgo?: number;
      toDate?: string;
    } = {
      ...originalQueryParams,
    };

    const {
      fromDate,
      toDate,
      dateAggregation = DEFAULT_QP_VALUES.hotel['short-term-rental']
        .dateAggregation,
      priceRangeStart,
      priceRangeEnd,
      refDaysAgo,
      radius,
    } = validatedQueryParams;

    const rangeStart =
      fromDate ??
      this.hotelAndMarketService.getLocalTime().format('YYYY-MM-DD');
    if (toDate && rangeStart && toDate < rangeStart) {
      validatedQueryParams['fromDate'] = rangeStart;
      validatedQueryParams['toDate'] = rangeStart;
    }

    // DateAggregation can only be day or week
    const validAggregationValues: string[] = [
      DATE_AGGREGATION.DAY,
      DATE_AGGREGATION.WEEK,
    ];
    if (dateAggregation && !validAggregationValues.includes(dateAggregation)) {
      validatedQueryParams['dateAggregation'] = DATE_AGGREGATION.DAY;
    }

    // Do not allow date range to be bigger than 12 months in the future
    if (
      toDate &&
      dayjs(toDate).diff(this.hotelAndMarketService.getLocalTime(), 'month') >=
        12
    ) {
      validatedQueryParams['toDate'] = this.hotelAndMarketService
        .getLocalTime()
        .add(12, 'month')
        .format('YYYY-MM-DD');
    }

    // Check priceRangeStart is less than priceRangeEnd
    if (
      priceRangeStart &&
      priceRangeEnd &&
      Number(priceRangeStart) > Number(priceRangeEnd)
    ) {
      validatedQueryParams['priceRangeEnd'] = priceRangeStart;
    }

    // Check refDaysAgo is between 1 and 14
    if (refDaysAgo && (Number(refDaysAgo) < 1 || Number(refDaysAgo) > 14)) {
      const newRefDaysAgo = Number(refDaysAgo) < 1 ? 1 : 14;
      validatedQueryParams['refDaysAgo'] = newRefDaysAgo;
    }

    // Check radius is between 1 and 50
    if (radius && (Number(radius) < 1 || Number(radius) > 50)) {
      const newRadius = Number(radius) < 1 ? 1 : 50;
      validatedQueryParams['radius'] = newRadius;
    }

    this.validateQueryParams(
      transition,
      originalQueryParams,
      validatedQueryParams,
      (transition as TransitionWithTarget).targetName,
    );
  }

  validateTotalRevenueQueryParams(
    transition: Transition,
    originalQueryParams: MarketTotalRevenueParams,
  ): void {
    const validatedQueryParams = { ...originalQueryParams };

    this.checkTotalRevenueView(validatedQueryParams);
    this.checkTotalRevenueFromToDates(validatedQueryParams);
    this.checkTotalRevenueReferencePeriods(validatedQueryParams);
    this.checkTotalRevenueTrendMetric(validatedQueryParams);
    this.checkTotalRevenueTrendAggregation(validatedQueryParams);
    this.checkTotalRevenueLocalReference(validatedQueryParams);

    this.validateQueryParams(
      transition,
      originalQueryParams,
      validatedQueryParams,
      'market.total-revenue',
    );
  }
}

declare module '@ember/service' {
  interface Registry {
    transition: TransitionService;
  }
}
